import {
  FEEDBACK_PATH,
  type CreateFeedbackIssueRequest,
  type CreateFeedbackIssueResponse,
} from './feedback'
import {
  normalizeAuthorMessage,
  normalizeRoom,
  normalizeRoomList,
  normalizeRoomMessages,
  unwrapRecord,
  type AuthorMessage,
  type Room,
} from './rooms'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

interface ApiOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
}

// ── Lead Gen types (exported for UI) ──

// ── Notification preferences ──

export type NotificationChannel = 'inApp' | 'email' | 'slack' | 'push'

export type NotificationEvent =
  | 'lead_found'
  | 'lead_scored_hot'
  | 'enrichment_complete'
  | 'skip_trace_complete'
  | 'deal_scored'
  | 'deal_stage_changed'
  | 'deal_closed'
  | 'campaign_sent'
  | 'campaign_response'
  | 'goal_reached'
  | 'decision_required'
  | 'agent_error'
  | 'agent_handoff'
  | 'budget_alert'
  | 'job_complete'
  | 'briefing_ready'
  | 'weekly_report'

export interface NotificationPrefs {
  channels: Record<NotificationChannel, boolean>
  events: Record<NotificationEvent, Record<NotificationChannel, boolean>>
}

export type LeadNextAction = 'enrich' | 'score' | 'skiptrace' | 'sequence' | 'wait_for_reply' | 'review' | 'pass'

export interface ScoutBuyBoxMatch {
  buyBoxId: string
  buyBoxName: string
  target: 'parcels' | 'listings'
  emoji: string | null
  matchCount: number
  newSinceLastRun: number
  sampleIds: string[]
}

export interface ScoutDiscoveryResult {
  orgId: string
  ranAt: string
  totalBoxes: number
  activeBoxes: number
  matches: ScoutBuyBoxMatch[]
  totalNewMatches: number
}

export interface QueuedLead {
  id: string
  address: string
  city: string | null
  state: string | null
  county: string | null
  ownerName: string | null
  score: number | null
  status: string
  phone: string | null
  email: string | null
  motivationSignals: string | null
  foundAt: string | null
  source: string | null
  sourceParcelId: string | null
  sourceListingId: string | null
  priorityScore: number
  nextAction: LeadNextAction
  reasons: string[]
}

export interface DashboardStats {
  kpis: {
    hotLeads: number
    totalLeads: number
    hotPct: number
    totalDeals: number
    activeDeals: number
    closedDeals: number
    pipelineValue: number
    realizedProfit: number
    avgLeadScore: number
    conversionRate: number
    weekDelta: number
    newWeek: number
    tokensUsed: number
    tokensLimit: number
    pendingApprovals: number
    activeBuyBoxes: number
  }
  funnel: {
    parcelsNew: number
    parcelsImported: number
    listingsNew: number
    leads: number
    hotLeads: number
    opportunities: number
    closed: number
  }
  inflow: Array<{ day: string; leads: number; listings: number; parcels: number }>
  byCounty: Array<{ county: string; count: number }>
  bySource: Array<{ source: string; count: number }>
  byStage: Array<{ stage: string; count: number; value: number }>
  listings: { total: number; new: number; hot: number }
  parcels: { total: number; new: number; imported: number; dismissed: number }
  outreach?: {
    sent24h: number
    sent7d: number
    received24h: number
    received7d: number
    replyRate: number
    sendDelta: number
    activeThreads: number
    awaitingReply: number
    unread: number
    activeIn24h: number
    activeCampaigns: number
    pausedCampaigns: number
    draftCampaigns: number
    totalEnrolled: number
    totalAppointments: number
    appointmentsUpcoming7d: number
    appointmentsUpcoming24h: number
    appointmentsCompleted7d: number
    appointmentsNoShow7d: number
    dncAdded7d: number
    byChannel: Array<{ channel: string; sent: number; replies: number }>
    daily: Array<{ day: string; sent: number; replies: number }>
    topCampaigns: Array<{
      id: string
      name: string
      status: string
      enrolled: number
      sent: number
      responded: number
      appointments: number
      responseRate: number | null
    }>
  }
}

export interface ApprovalItem {
  id: string
  agentRole: string
  type: string
  title: string
  summary: string
  reasoning: string | null
  confidence: number | null
  data: Record<string, unknown>
  suggestedAction: string | null
  suggestedValue: string | null
  alternatives: Array<{ label: string; value: string }>
  urgent: boolean
  estimatedTokens: number | null
  propertyAddress: string | null
  expiresAt: string | null
  createdAt: string
  status: 'pending' | 'approved' | 'rejected'
  resolvedAt: string | null
  resolvedBy: string | null
  /** Approved alternative value or rejection feedback, if any. */
  note: string | null
}

export interface ApprovalsResponse {
  /** Alias of `pending`, kept for older callers. */
  decisions: ApprovalItem[]
  pending: ApprovalItem[]
  resolved: ApprovalItem[]
  total: number
}

function parseMaybeJson<T>(v: unknown, fallback: T): T {
  if (v == null) return fallback
  if (typeof v !== 'string') return v as T
  try { return JSON.parse(v) as T } catch { return fallback }
}

function normalizeApproval(raw: unknown, fallbackStatus: ApprovalItem['status'] = 'pending'): ApprovalItem {
  const r = (raw || {}) as Record<string, any>
  const status: ApprovalItem['status'] =
    r.status === 'approved' || r.status === 'rejected' || r.status === 'pending' ? r.status
    : r.resolvedAt ? (String(r.resolution || '').startsWith('rejected') ? 'rejected' : 'approved')
    : fallbackStatus
  const alternatives = parseMaybeJson<unknown>(r.alternatives ?? r.alternativesJson, [])
  return {
    id: String(r.id),
    agentRole: r.agentRole || 'operator',
    type: r.type || 'decision',
    title: r.title || 'Decision',
    summary: r.summary || '',
    reasoning: r.reasoning ?? null,
    confidence: r.confidence ?? null,
    data: parseMaybeJson<Record<string, unknown>>(r.data ?? r.dataJson, {}),
    suggestedAction: r.suggestedAction ?? null,
    suggestedValue: r.suggestedValue ?? null,
    alternatives: Array.isArray(alternatives) ? alternatives as ApprovalItem['alternatives'] : [],
    urgent: !!r.urgent,
    estimatedTokens: r.estimatedTokens ?? null,
    propertyAddress: r.propertyAddress ?? null,
    expiresAt: r.expiresAt ?? null,
    createdAt: r.createdAt,
    status,
    resolvedAt: r.resolvedAt ?? null,
    resolvedBy: r.resolvedBy ?? null,
    note: r.note ?? null,
  }
}

export interface UnderwritingProfile {
  id: string
  orgId: string
  name: string
  description: string | null
  isDefault: boolean
  formulaType: 'mao_pct' | 'fixed_profit' | 'custom'
  maoPercent: number
  targetProfit: number
  repairMultiplier: number
  holdingCostPerMonth: number
  holdingMonths: number
  closingCostsPercent: number
  assignmentFee: number
  compMaxDistanceMiles: number
  compMaxAgeDays: number
  compSqftVariancePercent: number
  compMinCount: number
  convictionWeights: {
    comps: number
    equity: number
    motivation: number
    condition: number
  }
  notes: string | null
}

export interface TeamInvite {
  id: string
  email: string
  role: 'admin' | 'member' | 'viewer'
  invitedAt: string | null
  expiresAt: string | null
  /** Pre-built signup URL with the token embedded — copy + share. */
  link: string
}

/** One membership row for the multi-org sign-in picker / header switcher. */
export interface Membership {
  orgId: string
  orgName: string
  role: 'admin' | 'member' | 'viewer'
  plan: string
  /** Onboarding-call complete + plan chosen. */
  activated: boolean
  /** Soft-archived orgs are returned by /memberships but not by /signin. */
  archived?: boolean
  joinedAt: string
}

// ── Campaign deep-dive payload ──
//
// Shape of GET /outreach/campaigns/:id/detail. Mirrors the server
// projection 1:1 so the page can render directly off this type.

export interface CampaignDetail {
  campaign: {
    id: string
    name: string
    status: 'draft' | 'running' | 'paused' | 'completed'
    channels: string | null
    targetListId: string | null
    startedAt: string | null
    createdAt: string
    sendStartHour: number
    sendEndHour: number
    activeDays: string[]
    timezone: string
    autoReplyEnabled: boolean
    autoReplyConfidenceThreshold: number
    approvalRampCount: number
    autoRepliesSent: number
    approvedRepliesSent: number
    goal: string | null
    goalType: 'book_call' | 'get_offer' | 'qualify' | 'nurture' | 'custom'
    successCriteria: string[]
    disqualifyCriteria: string[]
    pauseCadenceOnReply: boolean
  }
  sequence: {
    totalSteps: number
    totalDays: number
    steps: Array<{
      stepNum: number
      channel: 'sms' | 'email'
      dayOffset: number
      templateId: string | null
      subject: string | null
      body: string
    }>
  }
  funnel: {
    enrolled: number
    inFlight: number
    completed: number
    responded: number
    optedOut: number
    sent: number
    delivered: number
    appointments: number
    tokensUsed: number
    responseRate: number
    deliveryRate: number
    /** Mean cadence depth — how far the drip got. Terminal enrollments
     *  count as 100%. Use goalCompletionRate for "did it work". */
    avgProgress: number
    /** % of enrolled contacts whose enrollment hit `completed`
     *  (goal reached per the conversation engine). */
    goalCompletionRate: number
  }
  stepDistribution: Array<{ stepNum: number; contacts: number }>
  statusDistribution: Record<string, number>
  perStepStats: Array<{
    campaignId: string
    stepNum: number
    channel: 'sms' | 'email'
    sent: number
    delivered: number
    responded: number
    avgResponseTimeSec: number | null
    deliveryRate: number | null
    responseRate: number | null
  }>
  cadence: {
    lastSendAt: string | null
    sendWindow: { start: number; end: number }
    activeDays: string[]
    timezone: string
  }
  engineStats: {
    awaitingApproval: number
    autoSent: number
    humanSent: number
    rejected: number
    failed: number
    goalReached: number
    goalBlocked: number
    progressing: number
    activeConversations: number
    opportunitiesCreated: number
  }
  opportunities: Array<{
    id: string
    address: string
    stage: 'lead' | 'contact_made' | 'offer_sent' | 'under_contract' | 'closed' | 'dead'
    createdAt: string
  }>
}

export interface CampaignConversation {
  analysisId: string
  threadId: string
  contactName: string | null
  contactAddress: string | null
  leadId: string | null
  contactId: string | null
  leadScore: number | null
  leadStatus: string | null
  enrollmentStatus: string | null
  currentStep: number | null
  lastMessageAt: string | null
  lastMessagePreview: string | null
  lastMessageDirection: 'in' | 'out' | null
  unreadCount: number
  analysis: {
    id: string
    intent: string
    confidence: number
    goalProgress: 'progressing' | 'reached' | 'blocked' | 'na'
    goalNote: string | null
    status: 'pending' | 'approved' | 'rejected' | 'edited' | 'sent' | 'auto_sent' | 'failed'
    policyReason: string | null
    suggestedReply: string | null
    suggestedSubject: string | null
    suggestedChannel: 'sms' | 'email' | null
    leadScoreAdjust: number
    signals: string[]
    stepNum: number | null
    createdAt: string
  }
}

export type LeadGenLeadType =
  | 'probate'
  | 'tax_lien'
  | 'pre_foreclosure'
  | 'absentee'
  | 'code_violation'
  | 'high_equity'

export type LeadGenPropertyType = 'sfr' | 'multi' | 'land' | 'commercial'

export interface LeadGenJobConfig {
  counties: string[]
  /** Optional city sub-filter scoped within `counties`. */
  cities?: string[]
  /** Motivation filters. Empty = geographic / property filters only. */
  leadTypes: LeadGenLeadType[]
  propertyTypes?: LeadGenPropertyType[]
  equityMin?: number
  valueMin?: number
  valueMax?: number
  yearBuiltMin?: number
  yearBuiltMax?: number
  outOfState?: boolean
  excludeCorporate?: boolean
  excludeContacted?: boolean
  maxLeads: number
}

export interface LeadGenJobEvent {
  ts: string
  type: 'progress' | 'log' | 'lead_found' | 'status'
  message: string
  data?: Record<string, unknown>
}

export interface LeadGenJob {
  id: string
  orgId: string
  userId: string
  /** Chat conversation that produced the spec — present for jobs created after this was added. */
  conversationId?: string
  status: 'queued' | 'claimed' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  message: string
  leadsFound: number
  config: LeadGenJobConfig
  events: LeadGenJobEvent[]
  resultListId?: string
  /** Worker hostname or id that claimed this job, once claimed. */
  claimedBy?: string
  claimedAt?: string
  error?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
}

export interface LeadGenStreamEvent {
  type: string
  data: unknown
}

// ── Buyer Gen types (parallel of LeadGen) ──

export type BuyerCategory =
  | 'cash'
  | 'fix_flip'
  | 'buy_hold'
  | 'wholesaler'
  | 'institutional'

export interface BuyerGenJobConfig {
  counties: string[]
  cities?: string[]
  buyerTypes: BuyerCategory[]
  propertyTypes?: LeadGenPropertyType[]
  minPurchases12mo?: number
  minPof?: number
  corporateOnly?: boolean
  outOfState?: boolean
  excludeContacted?: boolean
  maxBuyers: number
}

export interface BuyerGenJob {
  id: string
  orgId: string
  userId: string
  conversationId?: string
  status: 'queued' | 'claimed' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  message: string
  buyersFound: number
  config: BuyerGenJobConfig
  events: LeadGenJobEvent[]
  resultBuyerIds?: string[]
  claimedBy?: string
  claimedAt?: string
  error?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  promptSummary?: string
}

// ── Agent Gen ──

export type AgentDirectorySource = 'gamls' | 'fmls' | 'arello' | 'manual_url'

export interface AgentGenMarket {
  type: 'city' | 'county' | 'zip'
  value: string
}

export interface AgentGenJobConfig {
  state: string
  directorySource?: AgentDirectorySource
  startUrl?: string
  markets?: AgentGenMarket[]
  brokerage?: string[]
  specialties?: string[]
  minDealsClosed12mo?: number
  excludeExisting?: boolean
  maxAgents: number
}

export interface AgentGenJob {
  id: string
  orgId: string
  userId: string
  conversationId?: string
  status: 'queued' | 'claimed' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  message: string
  agentsFound: number
  config: AgentGenJobConfig
  events: LeadGenJobEvent[]
  resultContactIds?: string[]
  claimedBy?: string
  claimedAt?: string
  error?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  promptSummary?: string
}

// ── Parcel Gen types ──

export interface ParcelGenJobConfig {
  states: string[]
  counties: string[]
  cities?: string[]
  propertyTypes?: LeadGenPropertyType[]
  includeExempt?: boolean
  incremental?: boolean
  maxParcels: number
}

export interface ParcelGenJob {
  id: string
  orgId: string
  userId: string
  conversationId?: string
  status: 'queued' | 'claimed' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  message: string
  parcelsFound: number
  config: ParcelGenJobConfig
  events: LeadGenJobEvent[]
  resultDatasetId?: string
  claimedBy?: string
  claimedAt?: string
  error?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  promptSummary?: string
}

// ── Research types ──

export type ResearchTargetType = 'leads' | 'buyers' | 'contacts'
export type ResearchDepth = 'quick' | 'standard' | 'deep'
export type ResearchSource = 'skip_trace' | 'property_data' | 'motivation_signals' | 'owner_background' | 'comps'

export interface ResearchJobConfig {
  targetType: ResearchTargetType
  /** Either targetIds (direct record IDs) OR listId — exactly one is required. */
  targetIds?: string[]
  listId?: string
  depth: ResearchDepth
  /** Explicit source list; if omitted, defaults come from depth. */
  sources?: ResearchSource[]
}

export interface ResearchJob {
  id: string
  orgId: string
  userId: string
  conversationId?: string
  status: 'queued' | 'claimed' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number
  message: string
  recordsUpdated: number
  config: ResearchJobConfig
  events: LeadGenJobEvent[]
  updatedRecordIds: string[]
  claimedBy?: string
  claimedAt?: string
  error?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  promptSummary?: string
}

// ── Outreach inbox types ──

export interface OutreachThread {
  id: string
  orgId: string
  leadId: string
  campaignId: string | null
  ghlContactId: string | null
  contactName: string | null
  contactAddress: string | null
  /** Comma-separated channel list, e.g. "sms,email". */
  channels: string
  lastMessageAt: string
  lastMessagePreview: string | null
  lastMessageDirection: 'in' | 'out' | null
  unreadCount: number
  status: string
  createdAt: string
}

export interface OutreachMessage {
  id: string
  threadId: string
  orgId: string
  direction: 'in' | 'out'
  channel: 'sms' | 'email'
  body: string
  ghlMessageId: string | null
  agentRole: string | null
  createdAt: string
}

export type ResearchClaimType =
  | 'contact_info'
  | 'property_data'
  | 'divorce_filing'
  | 'probate_filing'
  | 'tax_lien'
  | 'pre_foreclosure'
  | 'code_violation'
  | 'owner_background'
  | 'related_person'
  | 'other'

export interface ResearchFinding {
  id: string
  claimType: ResearchClaimType
  claimPayload: Record<string, unknown>
  sourceTool: string
  sourceUrl?: string | null
  confidence: number
  status: 'candidate' | 'confirmed' | 'dismissed'
  linkedRecords: Array<{
    type: 'leads' | 'buyers' | 'contacts'
    id: string
    matchConfidence?: number
  }>
  discoveredAt: string
  confirmedAt?: string
  confirmedBy?: string | null
  dismissedAt?: string
  dismissedBy?: string | null
}

class ApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

class ForgeAPI {
  private token: string | null = null

  setToken(token: string) {
    this.token = token
    localStorage.setItem('forge-token', token)
  }

  clearToken() {
    this.token = null
    localStorage.removeItem('forge-token')
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('forge-token')
    }
    return this.token
  }

  /**
   * Decode the current JWT's payload — without verifying the
   * signature (the server is the only place that should do that).
   * Used by the multi-org picker to know which org the just-issued
   * token already targets, so we can skip a redundant /switch-org
   * round-trip when the user picks the default. Returns null if no
   * token, or if the token isn't a JWT shape.
   */
  getTokenPayload(): { id: string; email: string; orgId: string; role: string; plan: string } | null {
    const t = this.getToken()
    if (!t) return null
    const parts = t.split('.')
    if (parts.length !== 3) return null
    try {
      const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(json)
    } catch {
      return null
    }
  }

  private async fetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options
    const token = this.getToken()

    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Request failed' }))
      throw new ApiError(res.status, (error as { error?: string }).error || 'Request failed')
    }

    return res.json() as Promise<T>
  }

  // ── Auth ──

  async signIn(email: string, password: string) {
    const result = await this.fetch<{
      token: string
      user: { id: string; email: string; name: string; role: string; plan: string; orgId: string }
      memberships: Membership[]
    }>('/auth/signin', {
      method: 'POST',
      body: { email, password },
    })
    this.setToken(result.token)
    return result
  }

  /** Every org the current authed user belongs to. */
  async getMemberships() {
    return this.fetch<{
      activeOrgId: string
      memberships: Membership[]
    }>('/auth/memberships')
  }

  /**
   * Swap the active org for this session. Server validates membership
   * + mints a new JWT scoped to the target org. We swap the stored
   * token in place so subsequent requests target the new org without
   * a full sign-out/sign-in cycle.
   */
  async switchOrg(orgId: string) {
    const result = await this.fetch<{
      token: string
      user: { id: string; email: string; name: string; role: string; plan: string; orgId: string }
      orgName: string
    }>('/auth/switch-org', { method: 'POST', body: { orgId } })
    this.setToken(result.token)
    return result
  }

  async signUp(email: string, password: string, name: string, inviteToken?: string) {
    const result = await this.fetch<{ token: string; user: unknown }>('/auth/signup', {
      method: 'POST',
      body: { email, password, name, ...(inviteToken ? { inviteToken } : {}) },
    })
    this.setToken(result.token)
    return result
  }

  /**
   * Open the Google OAuth consent screen in a popup. Resolves with the
   * token + user payload once the callback page postMessages it back.
   * Rejects if the user closes the popup or auth fails.
   */
  async signInWithGoogle(mode: 'signin' | 'signup' = 'signin'): Promise<{
    token: string
    user: { id: string; email: string; name: string; role: string; plan: string; orgId: string; avatar?: string | null }
  }> {
    const { url } = await this.fetch<{ url: string }>('/auth/google/start', {
      method: 'POST',
      body: { mode },
    })
    return await new Promise((resolve, reject) => {
      const popup = window.open(url, 'forge-google-auth', 'width=520,height=640')
      if (!popup) {
        reject(new Error('Popup blocked — allow popups and try again'))
        return
      }
      const onMsg = (e: MessageEvent) => {
        const t = e.data?.type
        if (t === 'forge:auth:google:success' && e.data.payload?.token) {
          window.removeEventListener('message', onMsg)
          clearInterval(closedCheck)
          this.setToken(e.data.payload.token)
          resolve(e.data.payload)
        } else if (t === 'forge:auth:google:error') {
          window.removeEventListener('message', onMsg)
          clearInterval(closedCheck)
          reject(new Error(e.data.message ?? 'Google sign-in failed'))
        }
      }
      window.addEventListener('message', onMsg)
      const closedCheck = setInterval(() => {
        if (popup.closed) {
          clearInterval(closedCheck)
          window.removeEventListener('message', onMsg)
          reject(new Error('Sign-in cancelled'))
        }
      }, 500)
    })
  }

  async getMe() {
    // /api/auth/me actually returns { user: { ..., org: {...}, orgId, plan, ... }, tokenBalance }
    // The legacy `org` top-level was never populated; widening the type
    // so callers don't have to dig blindly.
    return this.fetch<{
      user: {
        id: string
        name: string
        email: string
        role: string
        plan: string
        orgId: string
        org?: {
          id: string
          name: string
          plan: string
          billingEmail?: string | null
          tokensUsed?: number
          tokensLimit?: number
          themeJson?: string | null
          createdAt?: string
        }
        tokensUsed?: number
        tokensLimit?: number
        createdAt?: string
      }
      tokenBalance?: unknown
    }>('/auth/me')
  }

  // ── Onboarding (post-signup gate) ──

  /**
   * Check whether this org has scheduled an onboarding call yet, and
   * whether the call has been completed (which activates the account).
   * Returns { activated, requestedAt } — both nullable. The frontend
   * uses this to decide whether to show the gate or jump to /home.
   */
  async getOnboardingStatus() {
    return this.fetch<{
      activated: boolean
      requestedAt: string | null
      callScheduledAt: string | null
      planChosenAt: string | null
    }>('/onboarding/status')
  }

  /**
   * Submit a call request from the /get-started gate. Records preferred
   * times + business context against the org and emails the operator.
   */
  async requestOnboarding(input: {
    preferredTimes: string[]
    phone: string
    counties?: string
    currentVolume?: string
    currentTools?: string
    notes?: string
  }) {
    return this.fetch<{
      ok: true
      requestedAt: string
    }>('/onboarding/request', {
      method: 'POST',
      body: input,
    })
  }

  // ── Theme / appearance ──

  async getOrgTheme() {
    return this.fetch<{ theme: Record<string, unknown> | null }>('/settings/theme')
  }

  /** Public theme for unauthenticated pages (landing, signin, signup).
   *  Returns the platform-default theme without requiring a JWT. */
  async getPublicTheme() {
    return this.fetch<{ theme: Record<string, unknown> | null }>('/theme/public')
  }

  // ── Token management (admin) ──

  async getTokenUsage() {
    return this.fetch<{
      org: {
        monthlyAllotment: number
        monthlyUsed: number
        purchasedBalance: number
        lastResetAt: string | null
        plan: string
      }
      cycleStart: string
      users: Array<{
        id: string
        name: string
        email: string
        role: string
        joinedAt: string | null
        used: number
        txCount: number
        monthlyTokenCap: number | null
        tokensFrozen: boolean
      }>
    }>('/settings/tokens/usage')
  }

  async getTokenTransactions(opts: { userId?: string; limit?: number } = {}) {
    const params = new URLSearchParams()
    if (opts.userId) params.set('userId', opts.userId)
    if (opts.limit != null) params.set('limit', String(opts.limit))
    const q = params.toString()
    return this.fetch<{
      transactions: Array<{
        id: string
        userId: string | null
        amount: number
        action: string
        resourceId: string | null
        createdAt: string
        userName: string | null
        userEmail: string | null
      }>
    }>(`/settings/tokens/transactions${q ? `?${q}` : ''}`)
  }

  async setOrgTokenLimit(monthlyAllotment: number) {
    return this.fetch<{ success: boolean; monthlyAllotment: number }>(
      '/settings/tokens/limit',
      { method: 'PUT', body: { monthlyAllotment } },
    )
  }

  async grantTokens(amount: number, reason?: string) {
    return this.fetch<{ success: boolean; granted: number }>(
      '/settings/tokens/grant',
      { method: 'POST', body: { amount, reason } },
    )
  }

  async resetOrgTokens() {
    return this.fetch<{ success: boolean }>('/settings/tokens/reset', { method: 'POST', body: {} })
  }

  async setUserTokenLimit(userId: string, opts: { monthlyTokenCap?: number | null; tokensFrozen?: boolean }) {
    return this.fetch<{ success: boolean }>(
      `/settings/tokens/users/${userId}`,
      { method: 'PUT', body: opts },
    )
  }

  async resetUserTokens(userId: string) {
    return this.fetch<{ success: boolean }>(
      `/settings/tokens/users/${userId}/reset`,
      { method: 'POST', body: {} },
    )
  }

  async updateOrgTheme(theme: Record<string, unknown>) {
    return this.fetch<{ success: boolean; theme: Record<string, unknown> }>(
      '/settings/theme',
      { method: 'PUT', body: theme },
    )
  }

  // ── Chat ──

  async getConversations() {
    const res = await this.fetch<Record<string, unknown>>('/chat/conversations')
    return (res as any)?.conversations ?? res
  }

  async createConversation(agentRole: string, title?: string, kind?: string) {
    return this.fetch<unknown>('/chat/conversations', {
      method: 'POST',
      body: { agentRole, title, kind },
    })
  }

  async getMessages(conversationId: string) {
    const res = await this.fetch<Record<string, unknown>>(`/chat/conversations/${conversationId}/messages`)
    return (res as any)?.messages ?? res
  }

  // Streaming chat message via SSE. Pass `signal` to abort mid-stream.
  async sendMessage(
    conversationId: string,
    content: string,
    onChunk: (text: string) => void,
    onDone: (messageId: string) => void,
    onToolEvent?: (event: { type: 'tool_call' | 'tool_result'; tool: string; input?: Record<string, unknown>; result?: unknown; error?: string; status: string }) => void,
    signal?: AbortSignal,
  ) {
    const token = this.getToken()
    const res = await fetch(`${API_BASE}/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content }),
      signal,
    })

    if (!res.ok) throw new ApiError(res.status, 'Failed to send message')

    const reader = res.body?.getReader()
    if (!reader) throw new Error('No response body')

    // When the caller aborts, cancel the reader so the loop below exits promptly.
    const onAbort = () => { reader.cancel().catch(() => {}) }
    signal?.addEventListener('abort', onAbort)

    const decoder = new TextDecoder()
    let buffer = ''

    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6)) as Record<string, unknown>
              if (data.type === 'text' && typeof data.text === 'string') onChunk(data.text)
              if ((data.type === 'tool_call' || data.type === 'tool_result') && typeof data.tool === 'string') onToolEvent?.(data as any)
              if (data.type === 'done' && typeof data.messageId === 'string') onDone(data.messageId)
            } catch {
              // skip malformed SSE data
            }
          }
        }
      }
    } finally {
      signal?.removeEventListener('abort', onAbort)
    }

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }
  }

  // ── Rooms (ops room + seat activity) ──

  /**
   * Try each path until one succeeds. A 404/405 means "try the next
   * shape" (`/rooms/ops/...` vs `/rooms/:id/...`). Other statuses throw.
   */
  private async fetchFirst(paths: string[], options: ApiOptions = {}): Promise<unknown> {
    let last: unknown
    for (const path of paths) {
      try {
        return await this.fetch(path, options)
      } catch (err) {
        last = err
        if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 405)) throw err
      }
    }
    throw last instanceof Error ? last : new ApiError(404, 'Request failed')
  }

  async listRooms(): Promise<Room[]> {
    const res = await this.fetch<unknown>('/rooms')
    return normalizeRoomList(res)
  }

  async getOpsRoom(): Promise<Room> {
    const res = await this.fetch<unknown>('/rooms/ops')
    const room = normalizeRoom(unwrapRecord(res, ['room', 'ops']) ?? res)
    if (!room) throw new ApiError(502, 'Ops room response was empty')
    return room
  }

  async getOpsMessages(roomId?: string): Promise<AuthorMessage[]> {
    const paths = ['/rooms/ops/messages']
    if (roomId && roomId !== 'ops') paths.push(`/rooms/${encodeURIComponent(roomId)}/messages`)
    const res = await this.fetchFirst(paths)
    return normalizeRoomMessages(res)
  }

  async postOpsMessage(content: string, roomId?: string): Promise<AuthorMessage | null> {
    const paths = ['/rooms/ops/messages']
    if (roomId && roomId !== 'ops') paths.push(`/rooms/${encodeURIComponent(roomId)}/messages`)
    const res = await this.fetchFirst(paths, { method: 'POST', body: { content } })
    const unwrapped = unwrapRecord(res, ['message', 'data'])
    return normalizeAuthorMessage(unwrapped ?? res)
  }

  async markOpsRead(roomId?: string): Promise<void> {
    const paths = ['/rooms/ops/mark-read']
    if (roomId && roomId !== 'ops') paths.push(`/rooms/${encodeURIComponent(roomId)}/mark-read`)
    await this.fetchFirst(paths, { method: 'POST', body: {} })
  }

  // ── Deals ──

  async getDeals(filters?: { stage?: string; county?: string }) {
    const params = new URLSearchParams()
    if (filters?.stage) params.set('stage', filters.stage)
    if (filters?.county) params.set('county', filters.county)
    const query = params.toString()
    const res = await this.fetch<Record<string, unknown>>(`/opportunities${query ? `?${query}` : ''}`)
    return (res as any)?.deals ?? res
  }

  async getDeal(id: string) {
    const res = await this.fetch<Record<string, unknown>>(`/opportunities/${id}`)
    return (res as any)?.deal ?? res
  }

  /** Run an Analyst evaluation on this opportunity — comps, ARV, MAO,
   *  repair estimate, conviction score. Uses the org's default
   *  underwriting profile unless `profileId` is provided. Operator
   *  overrides bypass analyst-pulled values and are recorded for audit.
   *  Writes the result back to the deal so the detail page reflects it
   *  on the next refetch. */
  async analyzeOpportunity(
    id: string,
    opts: {
      profileId?: string
      manualOverrides?: {
        arv?: number
        repairEstimate?: number
        motivationSignalCount?: number
        conditionScore?: number
      }
    } = {},
  ) {
    return this.fetch<{
      deal: any
      evaluation: any
      isMock: boolean
    }>(`/opportunities/${id}/analyze`, { method: 'POST', body: opts })
  }

  // ── Underwriting profiles ──

  async getUnderwritingProfiles() {
    return this.fetch<{ profiles: UnderwritingProfile[] }>('/underwriting/profiles')
  }

  async createUnderwritingProfile(payload: Partial<UnderwritingProfile> & { name: string }) {
    return this.fetch<{ profile: UnderwritingProfile }>('/underwriting/profiles', {
      method: 'POST',
      body: payload,
    })
  }

  async updateUnderwritingProfile(id: string, payload: Partial<UnderwritingProfile>) {
    return this.fetch<{ profile: UnderwritingProfile }>(`/underwriting/profiles/${id}`, {
      method: 'PUT',
      body: payload,
    })
  }

  async deleteUnderwritingProfile(id: string) {
    return this.fetch<{ success: boolean }>(`/underwriting/profiles/${id}`, { method: 'DELETE' })
  }

  async setDefaultUnderwritingProfile(id: string) {
    return this.fetch<{ success: boolean }>(`/underwriting/profiles/${id}/default`, {
      method: 'POST',
      body: {},
    })
  }

  async previewUnderwriting(payload: {
    profileId?: string
    arv: number
    repairEstimate: number
    estimatedEquity?: number | null
    equityPercent?: number | null
    motivationSignalCount?: number
    conditionScore?: number
    compTightness?: number
  }) {
    return this.fetch<{
      profile: UnderwritingProfile
      result: {
        mao: number
        convictionScore: number
        convictionLabel: 'strong_buy' | 'buy' | 'hold' | 'pass'
        estimatedProfit: number
        formulaSnapshot: Record<string, number>
        breakdown: Array<{ label: string; value: number; sign: '+' | '-' | '=' }>
      }
    }>('/underwriting/preview', { method: 'POST', body: payload })
  }

  async getOpportunityDetail(id: string) {
    return this.fetch<{
      deal: {
        id: string
        orgId: string
        address: string
        city: string | null
        county: string | null
        state: string | null
        zip: string | null
        ownerName: string | null
        ownerPhone: string | null
        ownerEmail: string | null
        stage: 'lead' | 'contact_made' | 'offer_sent' | 'under_contract' | 'closed' | 'dead'
        askingPrice: number | null
        offerPrice: number | null
        arv: number | null
        repairEstimate: number | null
        mao: number | null
        equityEstimate: number | null
        leadScore: number | null
        convictionScore: number | null
        source: string | null
        assignedAgent: string | null
        notes: string | null
        tags: string[]
        daysInStage: number
        createdAt: string
        updatedAt: string
        leadId: string | null
        campaignId: string | null
      }
      sourceCampaign: {
        id: string
        name: string
        status: string
        goal: string | null
        goalType: string | null
      } | null
      lead: any | null
      linkedContact: {
        id: string
        name: string | null
        phone: string | null
        email: string | null
        type: string | null
      } | null
      thread: {
        id: string
        status: string
        lastMessageAt: string
        lastMessagePreview: string | null
        lastMessageDirection: 'in' | 'out' | null
        unreadCount: number
      } | null
      latestAnalysis: {
        id: string
        status: string
        intent: string
        confidence: number
        goalProgress: 'progressing' | 'reached' | 'blocked' | 'na'
        goalNote: string | null
        suggestedReply: string | null
        signals: string[]
        createdAt: string
      } | null
      pendingDecisions: Array<{
        id: string
        agentRole: string
        type: string
        title: string
        summary: string
        confidence: number | null
        urgent: boolean
        propertyAddress: string | null
        createdAt: string
      }>
      recentActivity: Array<{
        id: string
        agentRole: string
        action: string
        detail: string | null
        tokensUsed: number
        createdAt: string
      }>
      recentEvaluation: {
        id: string
        arv: number | null
        repairEstimate: number | null
        mao70: number | null
        recommendedOffer: number | null
        estimatedProfit: number | null
        convictionScore: number | null
        convictionLabel: string | null
        analystNotes: string | null
        createdAt: string
        profileId: string | null
        formulaSnapshot: {
          profileName?: string
          formulaType?: string
          maoPercent?: number
          breakdown?: Array<{ label: string; value: number; sign: '+' | '-' | '=' }>
        } | null
        manualOverrides: {
          arv?: number
          repairEstimate?: number
          motivationSignalCount?: number
          conditionScore?: number
        } | null
      } | null
    }>(`/opportunities/${id}/detail`)
  }

  async createDeal(deal: unknown) {
    return this.fetch<unknown>('/opportunities', { method: 'POST', body: deal })
  }

  async updateDeal(id: string, updates: unknown) {
    return this.fetch<unknown>(`/opportunities/${id}`, { method: 'PUT', body: updates })
  }

  async updateDealStage(id: string, stage: string) {
    return this.fetch<unknown>(`/opportunities/${id}/stage`, { method: 'PATCH', body: { stage } })
  }

  async getDealStats() {
    return this.fetch<unknown>('/opportunities/stats')
  }

  async getDashboardStats() {
    return this.fetch<DashboardStats>('/operations/dashboard-stats')
  }

  // ── Lead Gen (job-based sourcing) ──

  async listLeadGenJobs() {
    const res = await this.fetch<{ jobs: LeadGenJob[] }>('/lead-gen/jobs')
    return res.jobs
  }

  async getLeadGenJob(id: string) {
    const res = await this.fetch<{ job: LeadGenJob }>(`/lead-gen/jobs/${id}`)
    return res.job
  }

  async createLeadGenJob(config: LeadGenJobConfig, conversationId?: string) {
    const res = await this.fetch<{ job: LeadGenJob }>('/lead-gen/jobs', {
      method: 'POST',
      body: conversationId ? { ...config, conversationId } : config,
    })
    return res.job
  }

  async cancelLeadGenJob(id: string) {
    const res = await this.fetch<{ job: LeadGenJob }>(`/lead-gen/jobs/${id}/cancel`, {
      method: 'POST',
    })
    return res.job
  }

  /** Reset a terminal lead-gen job back to queued so a worker can retry. */
  async requeueLeadGenJob(id: string) {
    return this.fetch<{ item: LeadGenJob }>(`/work/${id}/requeue`, {
      method: 'POST',
      body: { type: 'lead_gen' },
    })
  }

  // ── Buyer Gen (parallel API) ──

  async listBuyerGenJobs() {
    const res = await this.fetch<{ jobs: BuyerGenJob[] }>('/buyer-gen/jobs')
    return res.jobs
  }

  async getBuyerGenJob(id: string) {
    const res = await this.fetch<{ job: BuyerGenJob }>(`/buyer-gen/jobs/${id}`)
    return res.job
  }

  async createBuyerGenJob(config: BuyerGenJobConfig, conversationId?: string) {
    const res = await this.fetch<{ job: BuyerGenJob }>('/buyer-gen/jobs', {
      method: 'POST',
      body: conversationId ? { ...config, conversationId } : config,
    })
    return res.job
  }

  async cancelBuyerGenJob(id: string) {
    const res = await this.fetch<{ job: BuyerGenJob }>(`/buyer-gen/jobs/${id}/cancel`, {
      method: 'POST',
    })
    return res.job
  }

  async requeueBuyerGenJob(id: string) {
    return this.fetch<{ item: BuyerGenJob }>(`/work/${id}/requeue`, {
      method: 'POST',
      body: { type: 'buyer_gen' },
    })
  }

  // ── Agent Gen (parallel API) ──

  async listAgentGenJobs() {
    const res = await this.fetch<{ jobs: AgentGenJob[] }>('/agent-gen/jobs')
    return res.jobs
  }

  async getAgentGenJob(id: string) {
    const res = await this.fetch<{ job: AgentGenJob }>(`/agent-gen/jobs/${id}`)
    return res.job
  }

  async createAgentGenJob(config: AgentGenJobConfig, conversationId?: string) {
    const res = await this.fetch<{ job: AgentGenJob }>('/agent-gen/jobs', {
      method: 'POST',
      body: conversationId ? { ...config, conversationId } : config,
    })
    return res.job
  }

  async cancelAgentGenJob(id: string) {
    const res = await this.fetch<{ job: AgentGenJob }>(`/agent-gen/jobs/${id}/cancel`, {
      method: 'POST',
    })
    return res.job
  }

  async requeueAgentGenJob(id: string) {
    return this.fetch<{ item: AgentGenJob }>(`/work/${id}/requeue`, {
      method: 'POST',
      body: { type: 'agent_gen' },
    })
  }

  // ── Parcel Gen (parallel API) ──

  async listParcelGenJobs() {
    const res = await this.fetch<{ jobs: ParcelGenJob[] }>('/parcel-gen/jobs')
    return res.jobs
  }

  async getParcelGenJob(id: string) {
    const res = await this.fetch<{ job: ParcelGenJob }>(`/parcel-gen/jobs/${id}`)
    return res.job
  }

  async createParcelGenJob(config: ParcelGenJobConfig, conversationId?: string) {
    const res = await this.fetch<{ job: ParcelGenJob }>('/parcel-gen/jobs', {
      method: 'POST',
      body: conversationId ? { ...config, conversationId } : config,
    })
    return res.job
  }

  async cancelParcelGenJob(id: string) {
    const res = await this.fetch<{ job: ParcelGenJob }>(`/parcel-gen/jobs/${id}/cancel`, {
      method: 'POST',
    })
    return res.job
  }

  async requeueParcelGenJob(id: string) {
    return this.fetch<{ item: ParcelGenJob }>(`/work/${id}/requeue`, {
      method: 'POST',
      body: { type: 'parcel_gen' },
    })
  }

  // ── Research (parallel API) ──

  async listResearchJobs() {
    const res = await this.fetch<{ jobs: ResearchJob[] }>('/research/jobs')
    return res.jobs
  }

  async getResearchJob(id: string) {
    const res = await this.fetch<{ job: ResearchJob }>(`/research/jobs/${id}`)
    return res.job
  }

  async createResearchJob(config: ResearchJobConfig, conversationId?: string) {
    const res = await this.fetch<{ job: ResearchJob }>('/research/jobs', {
      method: 'POST',
      body: conversationId ? { ...config, conversationId } : config,
    })
    return res.job
  }

  async cancelResearchJob(id: string) {
    const res = await this.fetch<{ job: ResearchJob }>(`/research/jobs/${id}/cancel`, {
      method: 'POST',
    })
    return res.job
  }

  async requeueResearchJob(id: string) {
    return this.fetch<{ item: ResearchJob }>(`/work/${id}/requeue`, {
      method: 'POST',
      body: { type: 'research' },
    })
  }

  // ── Research findings — knowledge graph over records ──

  async listFindings(params: {
    recordType?: 'leads' | 'buyers' | 'contacts'
    recordId?: string
    status?: 'candidate' | 'confirmed' | 'dismissed'
    claimType?: string
    limit?: number
  } = {}) {
    const qs = new URLSearchParams()
    if (params.recordType) qs.set('recordType', params.recordType)
    if (params.recordId) qs.set('recordId', params.recordId)
    if (params.status) qs.set('status', params.status)
    if (params.claimType) qs.set('claimType', params.claimType)
    if (params.limit) qs.set('limit', String(params.limit))
    const res = await this.fetch<{ findings: ResearchFinding[]; count: number }>(`/research/findings${qs.toString() ? `?${qs}` : ''}`)
    return res.findings
  }

  async getFinding(id: string) {
    const res = await this.fetch<{ finding: ResearchFinding }>(`/research/findings/${id}`)
    return res.finding
  }

  async createFinding(body: {
    claimType: ResearchFinding['claimType']
    claimPayload: Record<string, unknown>
    sourceTool: string
    sourceUrl?: string
    confidence?: number
    linkedRecords: Array<{ type: 'leads' | 'buyers' | 'contacts'; id: string; matchConfidence?: number }>
    autoConfirm?: boolean
  }) {
    const res = await this.fetch<{ finding: ResearchFinding }>('/research/findings', {
      method: 'POST',
      body,
    })
    return res.finding
  }

  async confirmFinding(id: string) {
    return this.fetch<{ finding: ResearchFinding; recordsUpdated: number }>(`/research/findings/${id}/confirm`, {
      method: 'POST',
    })
  }

  async dismissFinding(id: string) {
    const res = await this.fetch<{ finding: ResearchFinding }>(`/research/findings/${id}/dismiss`, {
      method: 'POST',
    })
    return res.finding
  }


  /**
   * Subscribe to a job's progress stream. Returns an abort function;
   * call it to close the connection. Handles both Hono-style SSE
   * (`event:` + `data:`) and plain `data:`-only streams.
   */
  streamLeadGenJob(
    id: string,
    handlers: {
      onEvent?: (event: LeadGenStreamEvent) => void
      onDone?: (job: LeadGenJob) => void
      onError?: (err: unknown) => void
    },
  ): () => void {
    const token = this.getToken()
    const controller = new AbortController()

    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/lead-gen/jobs/${id}/stream`, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        })
        if (!res.ok || !res.body) {
          handlers.onError?.(new ApiError(res.status, `Stream failed: ${res.statusText}`))
          return
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          // Split into logical SSE records (separated by blank line).
          let sep = buffer.indexOf('\n\n')
          while (sep !== -1) {
            const record = buffer.slice(0, sep)
            buffer = buffer.slice(sep + 2)
            sep = buffer.indexOf('\n\n')

            let evtName: string | null = null
            const dataLines: string[] = []
            for (const line of record.split('\n')) {
              if (line.startsWith('event:')) evtName = line.slice(6).trim()
              else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
            }
            if (dataLines.length === 0) continue
            const dataStr = dataLines.join('\n')
            let parsed: unknown
            try { parsed = JSON.parse(dataStr) } catch { parsed = dataStr }

            if (evtName === 'done') {
              handlers.onDone?.(parsed as LeadGenJob)
            } else if (evtName !== 'ping') {
              handlers.onEvent?.({ type: evtName ?? 'message', data: parsed })
            }
          }
        }
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          handlers.onError?.(err)
        }
      }
    })()

    return () => controller.abort()
  }

  // ── Leads ──

  async getLeadLists() {
    const res = await this.fetch<Record<string, unknown>>('/leads/lists')
    return (res as any)?.lists ?? res
  }

  async getLeadListDetail(id: string) {
    return this.fetch<{ list: Record<string, unknown>; leads: Record<string, unknown>[] }>(`/leads/lists/${id}`)
  }

  async deleteLeadList(id: string) {
    return this.fetch<{ success: boolean }>(`/leads/lists/${id}`, { method: 'DELETE' })
  }

  // ── List composition (stack + cherry-pick) ──

  async composeLeadList(payload: {
    name: string
    fromListIds?: string[]
    leadIds?: string[]
    county?: string
    audience?: string
    tags?: string
  }) {
    return this.fetch<{ list: Record<string, unknown>; recordCount: number }>(
      '/leads/lists/compose',
      { method: 'POST', body: payload },
    )
  }

  async addLeadsToList(
    listId: string,
    payload: { leadIds?: string[]; fromListIds?: string[] },
  ) {
    return this.fetch<{ success: boolean; recordCount: number }>(
      `/leads/lists/${listId}/members`,
      { method: 'POST', body: payload },
    )
  }

  async removeLeadFromList(listId: string, leadId: string) {
    return this.fetch<{ success: boolean; recordCount: number }>(
      `/leads/lists/${listId}/members/${leadId}`,
      { method: 'DELETE' },
    )
  }

  // The leads router is mounted at /api/leads. These new endpoints
  // intentionally don't re-include "/leads" in their path so they live
  // at /api/leads/queue, /api/leads/advance/:id, etc. — avoiding the
  // route-shadowing the file's older "/leads/:id" pattern caused.
  async reEnrichLead(id: string) {
    return this.fetch<{ ok: boolean; leadId: string; sourceType: 'parcel' | 'listing'; fieldsUpdated: number }>(
      `/leads/re-enrich/${id}`,
      { method: 'POST' },
    )
  }

  async getLeadQueue(opts?: { limit?: number; action?: LeadNextAction; minPriority?: number }) {
    const qs = new URLSearchParams()
    if (opts?.limit != null) qs.set('limit', String(opts.limit))
    if (opts?.action) qs.set('action', opts.action)
    if (opts?.minPriority != null) qs.set('minPriority', String(opts.minPriority))
    return this.fetch<{
      queue: QueuedLead[]
      totals: { open: number; returned: number; byAction: Record<string, number> }
    }>(`/leads/queue${qs.toString() ? '?' + qs.toString() : ''}`)
  }

  async advanceLead(id: string, action?: LeadNextAction) {
    return this.fetch<{ ok: boolean; leadId: string; action: string; detail: string; updates: Record<string, unknown> }>(
      `/leads/advance/${id}`,
      { method: 'POST', body: action ? { action } : {} },
    )
  }

  async getScoutMatches() {
    return this.fetch<ScoutDiscoveryResult>('/scout/matches')
  }

  async discoverAndAcknowledge(buyBoxId?: string) {
    return this.fetch<ScoutDiscoveryResult>('/scout/discover', {
      method: 'POST',
      body: { acknowledge: true, buyBoxId },
    })
  }

  async bulkAdvanceLeads(opts: { ids?: string[]; action?: LeadNextAction; limit?: number; minPriority?: number }) {
    return this.fetch<{
      ok: boolean
      processed: number
      summary: { scored: number; skiptraceQueued: number; sequenced: number; enriched: number; passed: number }
      results: Array<{ id: string; action: string }>
    }>('/leads/bulk-advance', { method: 'POST', body: opts })
  }

  async getLead(id: string) {
    const res = await this.fetch<{
      lead: Record<string, unknown>
      sourceListing: {
        id: string
        address: string
        city: string | null
        state: string | null
        zip: string | null
        listPrice: number | null
        beds: number | null
        baths: number | null
        sqft: number | null
        yearBuilt: number | null
        photoUrl: string | null
        photoCount: number | null
        motivationScore: number | null
        valueAddScore: number | null
        listingAgentName: string | null
        externalIdsJson: string
        triageStatus: string
        listedAt: string | null
      } | null
      agentContact: {
        id: string
        name: string
        phone: string | null
        email: string | null
        type: string
        dealCount: number
        brokerage: string | null
        relationshipStage: string | null
      } | null
    }>(`/leads/leads/${id}`)
    return res
  }

  async updateLead(id: string, updates: Record<string, unknown>) {
    const res = await this.fetch<{ lead: Record<string, unknown> }>(`/leads/leads/${id}`, {
      method: 'PUT',
      body: updates,
    })
    return res.lead
  }

  async createLead(payload: {
    address: string
    city?: string
    county?: string
    state?: string
    zip?: string
    ownerName?: string
    phone?: string
    email?: string
    score?: number
    estimatedValue?: number
    estimatedEquity?: number
    equityPercent?: number
    propertyType?: string
    motivationSignals?: string
    notes?: string
    source?: string
    listId?: string
    contactId?: string
  }) {
    const res = await this.fetch<{ lead: Record<string, unknown> }>(`/leads/leads`, {
      method: 'POST',
      body: payload,
    })
    return res.lead
  }

  async getLeads(filters?: {
    listId?: string
    source?: string
    minScore?: number
    status?: string
    limit?: number
    offset?: number
  }) {
    const params = new URLSearchParams()
    if (filters?.listId) params.set('listId', filters.listId)
    if (filters?.source) params.set('source', filters.source)
    if (filters?.minScore) params.set('minScore', String(filters.minScore))
    if (filters?.status) params.set('status', filters.status)
    if (filters?.limit != null) params.set('limit', String(filters.limit))
    if (filters?.offset != null) params.set('offset', String(filters.offset))
    const query = params.toString()
    const res = await this.fetch<Record<string, unknown>>(`/leads/leads${query ? `?${query}` : ''}`)
    return (res as any)?.leads ?? res
  }

  /** Like getLeads but returns the full response envelope — needed when
   * the caller wants `total` for pagination instead of just the page slice. */
  async listLeads(filters?: {
    listId?: string
    source?: string
    minScore?: number
    status?: string
    limit?: number
    offset?: number
    search?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) {
    const params = new URLSearchParams()
    if (filters?.listId) params.set('listId', filters.listId)
    if (filters?.source) params.set('source', filters.source)
    if (filters?.minScore) params.set('minScore', String(filters.minScore))
    if (filters?.status) params.set('status', filters.status)
    if (filters?.limit != null) params.set('limit', String(filters.limit))
    if (filters?.offset != null) params.set('offset', String(filters.offset))
    if (filters?.search) params.set('search', filters.search)
    if (filters?.sortBy) params.set('sortBy', filters.sortBy)
    if (filters?.sortOrder) params.set('sortOrder', filters.sortOrder)
    const query = params.toString()
    return this.fetch<{ leads: Record<string, unknown>[]; total: number }>(
      `/leads/leads${query ? `?${query}` : ''}`,
    )
  }

  // Intersect: leads that appear on EVERY supplied list. Used by the
  // /leads/intersect page so a user can pick "absentee" + "vacant"
  // and see the leads that show up on both.
  async intersectLeadLists(listIds: string[], limit = 500) {
    const params = new URLSearchParams({ listIds: listIds.join(','), limit: String(limit) })
    return this.fetch<{
      leads: Record<string, unknown>[]
      lists: Array<{ id: string; name: string }>
      total: number
      error?: string
    }>(`/leads/leads/intersect?${params.toString()}`)
  }

  async uploadLeadList(file: File, county?: string, audience?: string): Promise<unknown> {
    const token = this.getToken()
    const formData = new FormData()
    formData.append('file', file)
    if (county) formData.append('county', county)
    if (audience) formData.append('audience', audience)

    const res = await fetch(`${API_BASE}/leads/lists`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // Don't set Content-Type — browser sets it with boundary for FormData
      },
      body: formData,
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Upload failed' }))
      throw new ApiError(res.status, (error as { error?: string }).error || 'Upload failed')
    }

    return res.json()
  }

  async scoreLeads(listId: string) {
    return this.fetch<unknown>('/leads/score', { method: 'POST', body: { listId } })
  }

  async skipTraceLeads(leadIds: string[]) {
    return this.fetch<unknown>('/leads/skip-trace', { method: 'POST', body: { leadIds } })
  }

  async getScoutConfig() {
    const res = await this.fetch<Record<string, unknown>>('/leads/scout/config')
    return (res as any)?.config ?? res
  }

  async updateScoutConfig(config: unknown) {
    return this.fetch<unknown>('/leads/scout/config', { method: 'PUT', body: config })
  }

  // ── Buyers ──

  async getBuyers(filters?: { search?: string; status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams()
    if (filters?.search) params.set('search', filters.search)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.limit != null) params.set('limit', String(filters.limit))
    if (filters?.offset != null) params.set('offset', String(filters.offset))
    const query = params.toString()
    return this.fetch<any>(`/buyers${query ? `?${query}` : ''}`)
  }

  async createBuyer(buyer: unknown) {
    return this.fetch<unknown>('/buyers', { method: 'POST', body: buyer })
  }

  async matchBuyers(dealId: string) {
    return this.fetch<unknown>(`/buyers/match/${dealId}`, { method: 'POST' })
  }

  async updateBuyer(id: string, updates: Record<string, unknown>) {
    return this.fetch<{ buyer: Record<string, unknown> }>(`/buyers/${id}`, {
      method: 'PUT',
      body: updates,
    })
  }

  async deleteBuyer(id: string) {
    return this.fetch<{ success: boolean }>(`/buyers/${id}`, { method: 'DELETE' })
  }

  async importBuyersCsv(file: File) {
    return this.uploadCsvImport('/buyers/import', file)
  }

  // ── Agents ──

  async getAgents(filters?: { search?: string; stage?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams()
    if (filters?.search) params.set('search', filters.search)
    if (filters?.stage) params.set('stage', filters.stage)
    if (filters?.limit != null) params.set('limit', String(filters.limit))
    if (filters?.offset != null) params.set('offset', String(filters.offset))
    const q = params.toString()
    return this.fetch<{
      agents: Array<Record<string, unknown>>
      total: number
      limit: number
      offset: number
      hasMore: boolean
    }>(`/agents${q ? `?${q}` : ''}`)
  }

  async getAgent(id: string) {
    const res = await this.fetch<{ agent: Record<string, unknown> }>(`/agents/${id}`)
    return res.agent
  }

  async createAgent(payload: Record<string, unknown>) {
    return this.fetch<{ agent: Record<string, unknown> }>('/agents', {
      method: 'POST',
      body: payload,
    })
  }

  async updateAgent(id: string, updates: Record<string, unknown>) {
    return this.fetch<{ agent: Record<string, unknown> }>(`/agents/${id}`, {
      method: 'PUT',
      body: updates,
    })
  }

  async deleteAgent(id: string) {
    return this.fetch<{ success: boolean }>(`/agents/${id}`, { method: 'DELETE' })
  }

  async importAgentsCsv(file: File) {
    return this.uploadCsvImport('/agents/import', file)
  }

  // ── Wholesalers ──

  async getWholesalers(filters?: { search?: string; stage?: string; partnered?: boolean; limit?: number; offset?: number }) {
    const params = new URLSearchParams()
    if (filters?.search) params.set('search', filters.search)
    if (filters?.stage) params.set('stage', filters.stage)
    if (filters?.partnered != null) params.set('partnered', String(filters.partnered))
    if (filters?.limit != null) params.set('limit', String(filters.limit))
    if (filters?.offset != null) params.set('offset', String(filters.offset))
    const q = params.toString()
    return this.fetch<{
      wholesalers: Array<Record<string, unknown>>
      total: number
      limit: number
      offset: number
      hasMore: boolean
    }>(`/wholesalers${q ? `?${q}` : ''}`)
  }

  async getWholesaler(id: string) {
    const res = await this.fetch<{ wholesaler: Record<string, unknown> }>(`/wholesalers/${id}`)
    return res.wholesaler
  }

  async createWholesaler(payload: Record<string, unknown>) {
    return this.fetch<{ wholesaler: Record<string, unknown> }>('/wholesalers', {
      method: 'POST',
      body: payload,
    })
  }

  async updateWholesaler(id: string, updates: Record<string, unknown>) {
    return this.fetch<{ wholesaler: Record<string, unknown> }>(`/wholesalers/${id}`, {
      method: 'PUT',
      body: updates,
    })
  }

  async deleteWholesaler(id: string) {
    return this.fetch<{ success: boolean }>(`/wholesalers/${id}`, { method: 'DELETE' })
  }

  async importWholesalersCsv(file: File) {
    return this.uploadCsvImport('/wholesalers/import', file)
  }

  // Shared CSV upload helper used by buyers/agents/wholesalers import.
  private async uploadCsvImport(path: string, file: File) {
    const token = this.getToken()
    const fd = new FormData()
    fd.append('file', file)
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      const err: any = new Error((body as any)?.error ?? `HTTP ${res.status}`)
      err.body = body
      throw err
    }
    return body as {
      total: number
      imported: number
      skipped: number
      failed: number
      errors: string[]
    }
  }

  // ── Outreach / Campaigns ──

  async getCampaigns() {
    const res = await this.fetch<Record<string, unknown>>('/outreach/campaigns')
    return (res as any)?.campaigns ?? res
  }

  async createCampaign(payload: {
    name: string
    channels?: string
    stepsJson?: string
    targetListId?: string
    contactCount?: number
    sendStartHour?: number
    sendEndHour?: number
    activeDays?: string[]
    timezone?: string
    goal?: string | null
    goalType?: 'book_call' | 'get_offer' | 'qualify' | 'nurture' | 'custom'
    successCriteria?: string[]
    disqualifyCriteria?: string[]
    pauseCadenceOnReply?: boolean
  }) {
    const res = await this.fetch<{ campaign: Record<string, unknown> }>('/outreach/campaigns', {
      method: 'POST',
      body: payload,
    })
    return res.campaign
  }

  /** PUT /outreach/campaigns/:id — update any subset of campaign fields. */
  async updateCampaign(
    id: string,
    payload: {
      name?: string
      status?: 'draft' | 'running' | 'paused' | 'completed'
      channels?: string
      stepsJson?: string
      targetListId?: string
      contactCount?: number
      sendStartHour?: number
      sendEndHour?: number
      activeDays?: string[]
      timezone?: string
      goal?: string | null
      goalType?: 'book_call' | 'get_offer' | 'qualify' | 'nurture' | 'custom'
      successCriteria?: string[]
      disqualifyCriteria?: string[]
      pauseCadenceOnReply?: boolean
    },
  ) {
    return this.fetch<{ campaign: Record<string, unknown> }>(
      `/outreach/campaigns/${id}`,
      { method: 'PUT', body: payload },
    )
  }

  /** Manually invoke the cadence engine (advance enrollments through
   *  their campaign's stepsJson). Production runs this every 15 min via
   *  BullMQ. */
  async tickCadence() {
    return this.fetch<{
      campaignsConsidered: number
      enrollmentsConsidered: number
      sentSucceeded: number
      sentFailed: number
      skippedNotDue: number
      skippedClosedWindow: number
      skippedDead: number
      errors: Array<{ campaignId: string; enrollmentId: string; reason: string }>
    }>('/outreach/cadence/tick', { method: 'POST' })
  }

  /**
   * Launch a campaign against its targetListId — pushes leads to GHL,
   * DNC-scrubs, enrolls into campaign_contacts, optionally fires a GHL
   * workflow per contact.
   *
   * `dryRun: true` skips the actual GHL push and returns the counts
   * that *would* be processed — use for the confirmation flow.
   */
  async launchCampaign(
    id: string,
    opts: { dryRun?: boolean; workflowId?: string; limit?: number } = {},
  ) {
    return this.fetch<{
      campaign?: Record<string, unknown>
      dryRun?: boolean
      ghlSource?: 'org_integration' | 'env'
      ghlLocationId?: string
      listSize?: number
      reachable?: number
      skippedDnc?: number
      alreadyEnrolled?: number
      wouldPush?: number
      pushed?: number
      succeeded?: number
      failed?: number
      errors?: Array<{ recordId?: string; message: string; type?: string }>
      workflowId?: string | null
      workflowTriggered?: number
      workflowNote?: string
    }>(`/outreach/campaigns/${id}/launch`, {
      method: 'POST',
      body: opts,
    })
  }

  async getCampaignStats(id: string) {
    return this.fetch<{
      campaignId: string
      name: string
      status: string
      stats: Record<string, number>
    }>(`/outreach/campaigns/${id}/stats`)
  }

  // ── Outreach / Inbox (threads + messages) ──

  async listOutreachThreads(opts: { limit?: number; status?: string } = {}) {
    const qs = new URLSearchParams()
    if (opts.limit) qs.set('limit', String(opts.limit))
    if (opts.status) qs.set('status', opts.status)
    const res = await this.fetch<{ threads: OutreachThread[] }>(
      `/outreach/threads${qs.toString() ? `?${qs}` : ''}`,
    )
    return res.threads
  }

  async getOutreachThread(id: string) {
    return this.fetch<{ thread: OutreachThread; messages: OutreachMessage[] }>(
      `/outreach/threads/${id}`,
    )
  }

  async sendOutreachMessage(
    threadId: string,
    body: string,
    opts: { channel?: 'sms' | 'email'; subject?: string } = {},
  ) {
    return this.fetch<{ message: OutreachMessage }>(
      `/outreach/threads/${threadId}/messages`,
      {
        method: 'POST',
        body: { body, channel: opts.channel, subject: opts.subject },
      },
    )
  }

  async markThreadRead(threadId: string) {
    return this.fetch<{ success: boolean }>(
      `/outreach/threads/${threadId}/read`,
      { method: 'POST' },
    )
  }

  // ── Campaign-enrolled contacts (Outreach > Contacts tab) ──

  async getOutreachContacts(filters?: {
    status?: string
    campaignId?: string
    search?: string
    limit?: number
    offset?: number
  }) {
    const params = new URLSearchParams()
    if (filters?.status) params.set('status', filters.status)
    if (filters?.campaignId) params.set('campaignId', filters.campaignId)
    if (filters?.search) params.set('search', filters.search)
    if (filters?.limit != null) params.set('limit', String(filters.limit))
    if (filters?.offset != null) params.set('offset', String(filters.offset))
    const q = params.toString()
    return this.fetch<{
      contacts: Array<{
        enrollmentId: string
        leadId: string
        campaignId: string
        campaignName: string
        campaignStatus: string
        currentStep: number | null
        enrollmentStatus: string
        lastSentAt: string | null
        respondedAt: string | null
        ghlContactId: string | null
        ownerName: string | null
        address: string | null
        city: string | null
        county: string | null
        phone: string | null
        email: string | null
        score: number | null
      }>
      count: number
    }>(`/outreach/contacts${q ? `?${q}` : ''}`)
  }

  // ── Per-step campaign analytics ──

  async getCampaignStepStats(campaignId: string) {
    return this.fetch<{
      campaignId: string
      steps: Array<{
        campaignId: string
        stepNum: number
        channel: 'sms' | 'email'
        templateId: string | null
        sent: number
        delivered: number
        responded: number
        responseLatencySumSec: number
        firstSentAt: string | null
        lastSentAt: string | null
        avgResponseTimeSec: number | null
        deliveryRate: number | null
        responseRate: number | null
      }>
    }>(`/outreach/campaigns/${campaignId}/step-stats`)
  }

  // ── Campaign deep-dive ──

  async getCampaignDetail(campaignId: string) {
    return this.fetch<CampaignDetail>(`/outreach/campaigns/${campaignId}/detail`)
  }

  async getCampaignContactsList(
    campaignId: string,
    opts: { status?: string; limit?: number; offset?: number } = {},
  ) {
    const params = new URLSearchParams()
    if (opts.status) params.set('status', opts.status)
    if (opts.limit != null) params.set('limit', String(opts.limit))
    if (opts.offset != null) params.set('offset', String(opts.offset))
    const q = params.toString()
    return this.fetch<{
      campaignId: string
      contacts: Array<{
        id: string
        leadId: string | null
        ghlContactId: string | null
        currentStep: number
        status: string
        lastSentAt: string | null
        respondedAt: string | null
        response: string | null
        leadName: string | null
        leadAddress: string | null
        leadPhone: string | null
        leadScore: number | null
        threadId: string | null
      }>
      pagination: { limit: number; offset: number; returned: number }
    }>(`/outreach/campaigns/${campaignId}/contacts${q ? `?${q}` : ''}`)
  }

  async getCampaignConversations(
    campaignId: string,
    opts: { filter?: 'all' | 'pending' | 'auto_sent' | 'reached' | 'blocked'; limit?: number } = {},
  ) {
    const params = new URLSearchParams()
    if (opts.filter) params.set('filter', opts.filter)
    if (opts.limit != null) params.set('limit', String(opts.limit))
    const q = params.toString()
    return this.fetch<{
      campaignId: string
      goal: string | null
      goalType: string
      conversations: CampaignConversation[]
    }>(`/outreach/campaigns/${campaignId}/conversations${q ? `?${q}` : ''}`)
  }

  /** Approve a pending analysis — sends the (optionally edited) reply via GHL. */
  async approveAnalysis(analysisId: string, opts: { editedReply?: string; editedSubject?: string } = {}) {
    return this.fetch<{ success: boolean; messageId: string }>(
      `/outreach/analyses/${analysisId}/approve`,
      { method: 'POST', body: opts },
    )
  }

  async rejectAnalysis(analysisId: string, reason?: string) {
    return this.fetch<{ success: boolean }>(
      `/outreach/analyses/${analysisId}/reject`,
      { method: 'POST', body: { reason } },
    )
  }

  async getCampaignTimeline(campaignId: string, limit = 50) {
    return this.fetch<{
      campaignId: string
      messages: Array<{
        id: string
        threadId: string
        direction: 'in' | 'out'
        channel: 'sms' | 'email'
        body: string
        agentRole: string | null
        createdAt: string
        contactName: string | null
        contactAddress: string | null
      }>
    }>(`/outreach/campaigns/${campaignId}/timeline?limit=${limit}`)
  }

  // ── Message templates ──

  async getTemplates(filters?: { channel?: 'sms' | 'email'; category?: string; search?: string }) {
    const params = new URLSearchParams()
    if (filters?.channel) params.set('channel', filters.channel)
    if (filters?.category) params.set('category', filters.category)
    if (filters?.search) params.set('search', filters.search)
    const q = params.toString()
    return this.fetch<{
      templates: Array<{
        id: string
        name: string
        channel: 'sms' | 'email'
        subject: string | null
        body: string
        category: string | null
        usageCount: number
        createdAt: string
        updatedAt: string
      }>
      count: number
    }>(`/templates${q ? `?${q}` : ''}`)
  }

  async getTemplate(id: string) {
    return this.fetch<{ template: Record<string, unknown> }>(`/templates/${id}`)
  }

  async createTemplate(payload: {
    name: string
    channel: 'sms' | 'email'
    body: string
    subject?: string
    category?: string
  }) {
    return this.fetch<{ template: Record<string, unknown> }>('/templates', {
      method: 'POST', body: payload,
    })
  }

  async updateTemplate(id: string, payload: Partial<{
    name: string
    channel: 'sms' | 'email'
    body: string
    subject: string
    category: string
  }>) {
    return this.fetch<{ template: Record<string, unknown> }>(`/templates/${id}`, {
      method: 'PUT', body: payload,
    })
  }

  async deleteTemplate(id: string) {
    return this.fetch<{ success: boolean }>(`/templates/${id}`, { method: 'DELETE' })
  }

  async renderTemplate(id: string, context?: Record<string, string>) {
    return this.fetch<{ rendered: { subject: string | null; body: string }; tokens: string[] }>(
      `/templates/${id}/render`,
      { method: 'POST', body: { context: context ?? {} } },
    )
  }

  // ── Conversation engine (per-thread analysis + approval queue) ──

  async getThreadAnalysis(threadId: string) {
    return this.fetch<{
      analysis: null | {
        id: string
        threadId: string
        intent: string
        confidence: number
        signals: string[]
        leadScoreAdjust: number
        suggestedReply: string | null
        suggestedSubject: string | null
        suggestedChannel: 'sms' | 'email' | null
        rationale: string | null
        status: 'pending' | 'approved' | 'rejected' | 'edited' | 'sent' | 'auto_sent' | 'failed'
        sentMessageId: string | null
        policyReason: string | null
        tokensUsed: number
        reviewedBy: string | null
        reviewedAt: string | null
        createdAt: string
      }
    }>(`/outreach/threads/${threadId}/analysis`)
  }

  async getPendingAnalyses(limit = 50) {
    return this.fetch<{
      analyses: Array<{
        id: string
        threadId: string
        intent: string
        confidence: number
        signals: string[]
        suggestedReply: string | null
        suggestedSubject: string | null
        suggestedChannel: 'sms' | 'email' | null
        rationale: string | null
        policyReason: string | null
        createdAt: string
      }>
      count: number
    }>(`/outreach/analyses/pending?limit=${limit}`)
  }

  async updateCampaignAutoReply(
    campaignId: string,
    settings: { autoReplyEnabled?: boolean; autoReplyConfidenceThreshold?: number; approvalRampCount?: number },
  ) {
    return this.fetch<{ campaign: Record<string, unknown> }>(
      `/outreach/campaigns/${campaignId}/auto-reply`,
      { method: 'PUT', body: settings },
    )
  }

  async generateAiSequence(payload: {
    audience: 'sellers' | 'buyers' | 'agents' | 'wholesalers'
    goal: 'cold_opener' | 'follow_up' | 'appointment_set' | 're_engagement' | 'introduction'
    channels: Array<'sms' | 'email'>
    length?: number
    tone?: 'warm' | 'professional' | 'urgent' | 'casual'
    context?: string
    /** Campaign-level goal that the conversation engine will drive toward
     *  once a contact replies. Each generated step ladders toward this. */
    goalStatement?: string
    goalType?: 'book_call' | 'get_offer' | 'qualify' | 'nurture' | 'custom'
    successCriteria?: string[]
  }) {
    return this.fetch<{
      name: string
      steps: Array<{
        channel: 'sms' | 'email'
        dayOffset: number
        subject?: string
        body: string
        rationale?: string
      }>
      meta: Record<string, unknown>
    }>('/templates/generate-sequence', { method: 'POST', body: payload })
  }

  async getGhlStatus() {
    return this.fetch<{
      connected: boolean
      source?: 'org_integration' | 'env'
      locationId?: string
      integrationId?: string | null
    }>('/outreach/ghl/status')
  }

  async pauseCampaign(id: string) {
    return this.fetch<unknown>(`/outreach/campaigns/${id}/pause`, { method: 'POST' })
  }

  async resumeCampaign(id: string) {
    return this.fetch<unknown>(`/outreach/campaigns/${id}/resume`, { method: 'POST' })
  }

  // ── Eval ──

  async evaluateProperty(address: string) {
    return this.fetch<unknown>('/eval', { method: 'POST', body: { address } })
  }

  async getEvaluation(address: string) {
    return this.fetch<unknown>(`/eval/${encodeURIComponent(address)}`)
  }

  // ── Briefing ──

  async getLatestBriefing() {
    const res = await this.fetch<Record<string, unknown>>('/briefing/latest')
    return (res as any)?.briefing ?? res
  }

  // ── Decisions ──

  async resolveDecision(id: string, action: string) {
    return this.fetch<unknown>(`/decisions/${id}/resolve`, {
      method: 'POST',
      body: { action },
    })
  }

  // ── Learning / Feedback ──

  async submitFeedback(payload: { type: string; decisionId: string; action: string; context?: unknown }) {
    return this.fetch<unknown>('/learning/feedback', {
      method: 'POST',
      body: payload,
    })
  }

  /**
   * File a bug or feature via REI-API.
   * POST /api/feedback — Platform owns GitHub token + labels (PR #28).
   */
  async createFeedbackIssue(input: CreateFeedbackIssueRequest) {
    return this.fetch<CreateFeedbackIssueResponse>(FEEDBACK_PATH, {
      method: 'POST',
      body: input,
    })
  }

  // ── Operations ──

  async getSystemHealth() {
    return this.fetch<unknown>('/operations/health')
  }

  async getTokenEconomics() {
    return this.fetch<unknown>('/operations/tokens')
  }

  async getAgentPerformance() {
    return this.fetch<unknown>('/operations/performance')
  }

  async getAuditTrail() {
    return this.fetch<unknown>('/operations/audit')
  }

  async pauseAgent(role: string) {
    return this.fetch<unknown>(`/scheduler/agents/${role}/pause`, { method: 'POST' })
  }

  async resumeAgent(role: string) {
    return this.fetch<unknown>(`/scheduler/agents/${role}/resume`, { method: 'POST' })
  }

  async runJob(jobId: string) {
    return this.fetch<unknown>(`/scheduler/jobs/${jobId}/run`, { method: 'POST' })
  }

  // ── Profile ──

  async updateProfile(data: { name?: string; email?: string; phone?: string; currentPassword?: string; newPassword?: string }) {
    return this.fetch<unknown>('/auth/profile', { method: 'PUT', body: data })
  }

  // ── Compliance ──

  async getComplianceDnc() {
    return this.fetch<unknown>('/compliance/dnc')
  }

  async saveComplianceSettings(settings: unknown) {
    return this.fetch<unknown>('/compliance/settings', { method: 'PUT', body: settings })
  }

  // ── Contracts ──

  async getContracts() {
    const res = await this.fetch<Record<string, unknown>>('/contracts')
    return (res as any)?.contracts ?? res
  }

  async createContract(data: unknown) {
    return this.fetch<unknown>('/contracts', { method: 'POST', body: data })
  }

  async sendContract(id: string) {
    return this.fetch<unknown>(`/contracts/${id}/send`, { method: 'POST' })
  }

  async voidContract(id: string) {
    return this.fetch<unknown>(`/contracts/${id}/void`, { method: 'POST' })
  }

  // ── Notifications ──

  async getNotifications() {
    const res = await this.fetch<Record<string, unknown>>('/notifications')
    return (res as any)?.notifications ?? res
  }

  async markNotificationRead(id: string) {
    return this.fetch<unknown>(`/notifications/${id}/read`, { method: 'PATCH' })
  }

  async markAllNotificationsRead() {
    return this.fetch<unknown>('/notifications/read-all', { method: 'POST' })
  }

  // ── Inbound ──

  async getInboundSources() {
    return this.fetch<unknown[]>('/inbound/sources')
  }

  async getInboundLeads() {
    return this.fetch<unknown[]>('/inbound/leads')
  }

  // ── Integrations ──

  async getIntegrations() {
    const res = await this.fetch<Record<string, unknown>>('/integrations')
    return (res as any)?.integrations ?? res
  }

  async createIntegration(payload: {
    platform: string
    name: string
    authType?: string
    credentials: Record<string, string>
    syncFrequency?: 'realtime' | 'every_5min' | 'every_hour' | 'daily' | 'manual'
  }) {
    const res = await this.fetch<{ integration: Record<string, unknown> }>('/integrations', {
      method: 'POST',
      body: payload,
    })
    return res.integration
  }

  async testIntegration(id: string) {
    return this.fetch<{
      result: { connected: boolean; error?: string }
      status: 'connected' | 'error'
    }>(`/integrations/${id}/test`, { method: 'POST' })
  }

  async deleteIntegration(id: string) {
    return this.fetch<{ success: boolean }>(`/integrations/${id}`, { method: 'DELETE' })
  }

  // ── Contacts ──

  async getContacts(filters?: { search?: string; source?: string; type?: string; minScore?: number; limit?: number; offset?: number }): Promise<any> {
    const params = new URLSearchParams()
    if (filters?.search) params.set('search', filters.search)
    if (filters?.source) params.set('source', filters.source)
    if (filters?.type) params.set('type', filters.type)
    if (filters?.minScore) params.set('minScore', String(filters.minScore))
    if (filters?.limit != null) params.set('limit', String(filters.limit))
    if (filters?.offset != null) params.set('offset', String(filters.offset))
    const query = params.toString()
    return this.fetch<any>(`/contacts${query ? `?${query}` : ''}`)
  }

  async getContact(id: string) {
    const res = await this.fetch<Record<string, unknown>>(`/contacts/${id}`)
    return (res as any)?.contact ?? res
  }

  async createContact(data: unknown) {
    return this.fetch<unknown>('/contacts', { method: 'POST', body: data })
  }

  async updateContact(id: string, data: unknown) {
    return this.fetch<unknown>(`/contacts/${id}`, { method: 'PUT', body: data })
  }

  async deleteContact(id: string) {
    return this.fetch<unknown>(`/contacts/${id}`, { method: 'DELETE' })
  }

  // ── Contact conversation (GHL-backed SMS/email) ──

  async getContactMessages(id: string) {
    return this.fetch<{
      threadId: string | null
      ghlContactId: string | null
      status: string | null
      unreadCount: number
      lastMessageAt: string | null
      messages: Array<{
        id: string
        direction: 'in' | 'out'
        channel: 'sms' | 'email'
        body: string
        agentRole: string | null
        createdAt: string
      }>
    }>(`/contacts/${id}/messages`)
  }

  async sendContactMessage(
    id: string,
    payload: { channel: 'sms' | 'email'; body: string; subject?: string },
  ) {
    return this.fetch<{
      ok: true
      threadId: string
      messageId: string
      ghlMessageId: string | null
      ghlContactId: string
    }>(`/contacts/${id}/messages`, { method: 'POST', body: payload })
  }

  async markContactMessagesRead(id: string) {
    return this.fetch<{ success: boolean }>(`/contacts/${id}/messages/read`, { method: 'POST' })
  }

  // ── Contact lists (membership-based, type-mixed) ──

  async getContactLists() {
    const res = await this.fetch<{
      lists: Array<{
        id: string
        name: string
        description: string | null
        purpose: string | null
        recordCount: number
        createdAt: string
        typeBreakdown: Record<string, number>
      }>
    }>('/contacts/lists')
    return res.lists
  }

  async getContactListDetail(id: string) {
    return this.fetch<{
      list: Record<string, unknown>
      contacts: Record<string, unknown>[]
    }>(`/contacts/lists/${id}`)
  }

  async createContactList(payload: {
    name: string
    description?: string
    purpose?: string
    tags?: string
  }) {
    return this.fetch<{ list: Record<string, unknown> }>(
      '/contacts/lists',
      { method: 'POST', body: payload },
    )
  }

  async composeContactList(payload: {
    name: string
    description?: string
    purpose?: string
    fromListIds?: string[]
    contactIds?: string[]
    filterType?: 'seller' | 'buyer' | 'agent' | 'wholesaler' | 'other'
  }) {
    return this.fetch<{ list: Record<string, unknown>; recordCount: number }>(
      '/contacts/lists/compose',
      { method: 'POST', body: payload },
    )
  }

  async addContactsToList(
    listId: string,
    payload: {
      contactIds?: string[]
      fromListIds?: string[]
      filterType?: 'seller' | 'buyer' | 'agent' | 'wholesaler' | 'other'
    },
  ) {
    return this.fetch<{ success: boolean; recordCount: number }>(
      `/contacts/lists/${listId}/members`,
      { method: 'POST', body: payload },
    )
  }

  async removeContactFromList(listId: string, contactId: string) {
    return this.fetch<{ success: boolean; recordCount: number }>(
      `/contacts/lists/${listId}/members/${contactId}`,
      { method: 'DELETE' },
    )
  }

  async deleteContactList(id: string) {
    return this.fetch<{ success: boolean }>(`/contacts/lists/${id}`, { method: 'DELETE' })
  }

  // ── Type-specific contact profiles ──

  async getContactProfile(contactId: string, type: 'buyer' | 'agent' | 'wholesaler') {
    const res = await this.fetch<{ profile: Record<string, unknown> | null }>(
      `/contacts/${contactId}/profile/${type}`,
    )
    return res.profile
  }

  async upsertContactProfile(
    contactId: string,
    type: 'buyer' | 'agent' | 'wholesaler',
    data: Record<string, unknown>,
  ) {
    return this.fetch<{ profile: Record<string, unknown> }>(
      `/contacts/${contactId}/profile/${type}`,
      { method: 'PUT', body: data },
    )
  }

  async deleteContactProfile(contactId: string, type: 'buyer' | 'agent' | 'wholesaler') {
    return this.fetch<{ success: boolean }>(
      `/contacts/${contactId}/profile/${type}`,
      { method: 'DELETE' },
    )
  }

  // ── Agent Settings ──

  async getAgentSettings() {
    return this.fetch<unknown>('/agent-settings')
  }

  async getAgentSettingsForRole(role: string) {
    return this.fetch<unknown>(`/agent-settings/${role}`)
  }

  async updateAgentSettings(role: string, settings: unknown) {
    return this.fetch<unknown>(`/agent-settings/${role}`, { method: 'PUT', body: settings })
  }

  async resetAgentSettings(role: string) {
    return this.fetch<unknown>(`/agent-settings/${role}/reset`, { method: 'POST' })
  }

  async getAgentUsage(role: string) {
    return this.fetch<unknown>(`/agent-settings/${role}/usage`)
  }

  // ── Guardrails ──

  async getGuardrails() {
    return this.fetch<{ config: Record<string, unknown>; tokenUsage: { dailyUsed: number; monthlyUsed: number } }>('/guardrails')
  }

  async updateGuardrails(updates: Record<string, unknown>) {
    return this.fetch<{ config: Record<string, unknown> }>('/guardrails', { method: 'PUT', body: updates })
  }

  async toggleKillSwitch(active: boolean) {
    return this.fetch<{ globalKillSwitch: boolean }>('/guardrails/kill-switch', { method: 'POST', body: { active } })
  }

  async toggleSandboxMode(active: boolean) {
    return this.fetch<{ sandboxMode: boolean }>('/guardrails/sandbox', { method: 'POST', body: { active } })
  }

  async getBlacklist() {
    const res = await this.fetch<Record<string, unknown>>('/guardrails/blacklist')
    return (res as any)?.blacklist ?? res
  }

  async addToBlacklist(type: 'phone' | 'email' | 'address', value: string) {
    return this.fetch<unknown>('/guardrails/blacklist', { method: 'POST', body: { type, value } })
  }

  async removeFromBlacklist(id: string) {
    return this.fetch<unknown>(`/guardrails/blacklist/${id}`, { method: 'DELETE' })
  }

  async getGuardrailRateLimits() {
    return this.fetch<{ rateLimits: Record<string, number> }>('/guardrails/rate-limits')
  }

  async getGuardrailAudit(page = 1, limit = 50) {
    return this.fetch<{ entries: unknown[]; total: number; page: number; pages: number }>(`/guardrails/audit?page=${page}&limit=${limit}`)
  }

  async testGuardrailAction(payload: { agentRole: string; action: string; amount?: number; contactPhone?: string; contactEmail?: string; county?: string; tokenCost?: number }) {
    return this.fetch<{ result: { allowed: boolean; reason?: string; requiresApproval?: boolean } }>('/guardrails/test', { method: 'POST', body: payload })
  }

  // ── Scheduler ──

  async getSchedulerStatus() {
    return this.fetch<unknown>('/scheduler/status')
  }

  // ── Smart Import ──

  async previewImport(
    file: File,
    county?: string,
    audience?: string,
  ): Promise<unknown> {
    const token = this.getToken()
    const formData = new FormData()
    formData.append('file', file)
    if (county) formData.append('county', county)
    if (audience) formData.append('audience', audience)

    const res = await fetch(`${API_BASE}/import/preview`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    })
    if (!res.ok) {
      const error = await res
        .json()
        .catch(() => ({ error: 'Preview failed' }))
      throw new ApiError(
        res.status,
        (error as { error?: string }).error || 'Preview failed',
      )
    }
    return res.json()
  }

  async executeImport(
    file: File,
    county?: string,
    audience?: string,
  ): Promise<unknown> {
    const token = this.getToken()
    const formData = new FormData()
    formData.append('file', file)
    if (county) formData.append('county', county)
    if (audience) formData.append('audience', audience)

    const res = await fetch(`${API_BASE}/import/execute`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    })
    if (!res.ok) {
      const error = await res
        .json()
        .catch(() => ({ error: 'Import failed' }))
      throw new ApiError(
        res.status,
        (error as { error?: string }).error || 'Import failed',
      )
    }
    return res.json()
  }

  async executeImportBySession(
    sessionId: string,
    onProgress?: (imported: number, total: number, phase: string) => void,
  ): Promise<unknown> {
    const token = this.getToken()
    const formData = new FormData()
    formData.append('sessionId', sessionId)

    const res = await fetch(`${API_BASE}/import/execute`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    })
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Import failed' }))
      throw new ApiError(res.status, (error as { error?: string }).error || 'Import failed')
    }

    // Parse SSE stream for real-time progress
    const reader = res.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''
    let finalResult: unknown = null

    for (;;) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6)) as Record<string, unknown>
            if (data.type === 'progress') {
              onProgress?.(data.imported as number, data.total as number, data.phase as string)
            } else if (data.type === 'done') {
              finalResult = data.result
            } else if (data.type === 'error') {
              throw new Error(data.error as string)
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'Import failed') throw e
          }
        }
      }
    }

    return { result: finalResult }
  }

  // ── Tool Store ──

  async getToolStoreItems(filters?: { category?: string; source?: string; agent?: string }) {
    const params = new URLSearchParams()
    if (filters?.category) params.set('category', filters.category)
    if (filters?.source) params.set('source', filters.source)
    if (filters?.agent) params.set('agent', filters.agent)
    const query = params.toString()
    return this.fetch<unknown>(`/tool-store${query ? `?${query}` : ''}`)
  }

  async registerToolStoreItem(data: unknown) {
    return this.fetch<unknown>('/tool-store', { method: 'POST', body: data })
  }

  async getToolStoreItem(id: string) {
    return this.fetch<unknown>(`/tool-store/${id}`)
  }

  async updateToolStoreItem(id: string, updates: unknown) {
    return this.fetch<unknown>(`/tool-store/${id}`, { method: 'PUT', body: updates })
  }

  async deleteToolStoreItem(id: string) {
    return this.fetch<unknown>(`/tool-store/${id}`, { method: 'DELETE' })
  }

  async testToolStoreItem(id: string, input: unknown) {
    return this.fetch<unknown>(`/tool-store/${id}/test`, { method: 'POST', body: { input } })
  }

  async enableToolStoreItem(id: string) {
    return this.fetch<unknown>(`/tool-store/${id}/enable`, { method: 'POST' })
  }

  async disableToolStoreItem(id: string) {
    return this.fetch<unknown>(`/tool-store/${id}/disable`, { method: 'POST' })
  }

  async updateToolStoreTraining(id: string, training: unknown) {
    return this.fetch<unknown>(`/tool-store/${id}/training`, { method: 'PUT', body: training })
  }

  async addToolStoreExample(id: string, example: unknown) {
    return this.fetch<unknown>(`/tool-store/${id}/training/examples`, { method: 'POST', body: example })
  }

  async removeToolStoreExample(id: string, index: number) {
    return this.fetch<unknown>(`/tool-store/${id}/training/examples/${index}`, { method: 'DELETE' })
  }

  async getToolStoreMCPServers() {
    return this.fetch<unknown>('/tool-store/mcp-servers')
  }

  async connectToolStoreMCPServer(data: { name: string; url: string; machineId?: string }) {
    return this.fetch<unknown>('/tool-store/mcp-servers', { method: 'POST', body: data })
  }

  async disconnectToolStoreMCPServer(id: string) {
    return this.fetch<unknown>(`/tool-store/mcp-servers/${id}`, { method: 'DELETE' })
  }

  async discoverToolStoreMCPTools(id: string) {
    return this.fetch<unknown>(`/tool-store/mcp-servers/${id}/discover`, { method: 'POST' })
  }

  async pingToolStoreMCPServer(id: string) {
    return this.fetch<unknown>(`/tool-store/mcp-servers/${id}/ping`, { method: 'POST' })
  }

  async getToolStoreStats() {
    return this.fetch<unknown>('/tool-store/stats')
  }

  // ── Plans ──
  async getPlans() { return this.fetch<any>('/plans') }
  async approvePlan(id: string) { return this.fetch<any>(`/plans/${id}/approve`, { method: 'PUT' }) }
  async executePlan(id: string) { return this.fetch<any>(`/plans/${id}/execute`, { method: 'POST' }) }

  // ── Swarms ──
  async getSwarms() { return this.fetch<any>('/swarms') }
  async createSwarm(data: Record<string, unknown>) { return this.fetch<any>('/swarms', { method: 'POST', body: JSON.stringify(data) }) }
  async getSwarmStatus(id: string) { return this.fetch<any>(`/swarms/${id}/status`) }

  // ── Skills ──
  async getSkills() { return this.fetch<any>('/skills') }
  async invokeSkill(name: string, args: Record<string, string>) { return this.fetch<any>(`/skills/${name}/invoke`, { method: 'POST', body: JSON.stringify({ args }) }) }

  // ── Costs ──
  async getSessionCosts() { return this.fetch<any>('/costs/session') }
  async getAgentCosts() { return this.fetch<any>('/costs/agents') }
  async getCostWarnings() { return this.fetch<any>('/costs/warnings') }

  // ── Memory ──
  async getMemories(params?: Record<string, string>) {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return this.fetch<any>(`/memory${qs}`)
  }
  async createMemory(data: Record<string, unknown>) { return this.fetch<any>('/memory', { method: 'POST', body: JSON.stringify(data) }) }
  async deleteMemory(id: string) { return this.fetch<any>(`/memory/${id}`, { method: 'DELETE' }) }

  // ── Sessions ──
  async getSessions(params?: Record<string, string>) {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return this.fetch<any>(`/sessions${qs}`)
  }
  async getSessionDetail(id: string) { return this.fetch<any>(`/sessions/${id}`) }

  // ── Subagents ──
  async getSubagents() { return this.fetch<any>('/subagents') }
  async spawnSubagent(id: string, data: any) { return this.fetch<any>(`/subagents/${id}/spawn`, { method: 'POST', body: JSON.stringify(data) }) }

  // ── Permissions ──
  async getPermissions() { return this.fetch<any>('/permissions') }
  async setPermissionMode(mode: string) { return this.fetch<any>('/permissions/mode', { method: 'POST', body: JSON.stringify({ mode }) }) }

  // ── Context ──
  async getAwaySummary() { return this.fetch<any>('/context/away') }

  // ── Auth / Password ──

  async forgotPassword(email: string) {
    return this.fetch<any>('/auth/forgot-password', { method: 'POST', body: { email } })
  }

  async resetPassword(token: string, newPassword: string) {
    return this.fetch<any>('/auth/reset-password', { method: 'POST', body: { token, newPassword } })
  }

  // ── Decisions / Activity / Stats ──

  async getPendingDecisions(): Promise<ApprovalsResponse> {
    const res = await this.fetch<Partial<ApprovalsResponse> & { pending?: unknown[]; resolved?: unknown[] }>('/approvals/pending')
    // Older API builds return raw rows (dataJson / alternativesJson strings,
    // no status) and no `decisions` key. Normalize both shapes.
    const pending = (res.pending || res.decisions || []).map((r) => normalizeApproval(r))
    const resolved = (res.resolved || []).map((r) => normalizeApproval(r, 'approved'))
    return { pending, decisions: pending, resolved, total: pending.length }
  }

  async approveDecision(id: string, value?: string) {
    return this.fetch<{ success: boolean; id: string; status: 'approved' }>(`/approvals/${id}/approve`, {
      method: 'POST',
      body: value ? { value } : {},
    })
  }

  async rejectDecision(id: string, feedback?: string) {
    return this.fetch<{ success: boolean; id: string; status: 'rejected' }>(`/approvals/${id}/reject`, {
      method: 'POST',
      body: feedback ? { feedback } : {},
    })
  }

  async getAgentActivity(limit?: number) {
    const qs = limit != null ? `?limit=${limit}` : ''
    return this.fetch<any>(`/operations/activity${qs}`)
  }

  async getPipelineStats() {
    return this.fetch<any>('/opportunities/pipeline-stats')
  }

  async getToolStatus() {
    return this.fetch<any>('/operations/tools')
  }

  async getUsageAlerts() {
    return this.fetch<any>('/operations/usage-alerts')
  }

  async getLeadStats() {
    // Lead router is mounted at /api/leads, the stats route is /leads/stats
    // *inside* it — so the full path is /api/leads/leads/stats.
    return this.fetch<any>('/leads/leads/stats')
  }

  async getLeadQuality() {
    // Lead router is mounted at /api/leads, the quality route is /leads/quality
    // *inside* it — so the full path is /api/leads/leads/quality.
    return this.fetch<{
      total: number
      completeness: Record<string, { count: number; percent: number }>
      recommendations: unknown[]
    }>('/leads/leads/quality')
  }

  // ── Events / activity feed ──

  async getEvents(limit = 50, type?: string) {
    const qs = new URLSearchParams({ limit: String(limit) })
    if (type) qs.set('type', type)
    return this.fetch<{
      events: Array<{
        id: string
        type: string
        data: Record<string, unknown>
        orgId: string
        createdAt: string
      }>
      count: number
    }>(`/events?${qs.toString()}`)
  }

  /**
   * Subscribe to the global event stream. Returns an unsubscribe fn.
   * Events arrive as { type, data, id, timestamp }.
   */
  openEventStream(
    handlers: {
      onEvent?: (event: { type: string; data: Record<string, unknown>; id: string; timestamp: string }) => void
      onError?: (err: unknown) => void
    },
  ): () => void {
    const token = this.getToken()
    const controller = new AbortController()

    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/events/stream`, {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        })
        if (!res.ok || !res.body) {
          handlers.onError?.(new ApiError(res.status, `Stream failed: ${res.statusText}`))
          return
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          let sep = buffer.indexOf('\n\n')
          while (sep !== -1) {
            const record = buffer.slice(0, sep)
            buffer = buffer.slice(sep + 2)
            sep = buffer.indexOf('\n\n')

            let evtName: string | null = null
            const dataLines: string[] = []
            for (const line of record.split('\n')) {
              if (line.startsWith('event:')) evtName = line.slice(6).trim()
              else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
            }
            if (dataLines.length === 0) continue
            const dataStr = dataLines.join('\n')
            let parsed: any
            try { parsed = JSON.parse(dataStr) } catch { continue }

            if (evtName === 'ping' || evtName === 'connected') continue
            handlers.onEvent?.({
              type: parsed.type ?? evtName ?? 'message',
              data: parsed.data ?? {},
              id: parsed.id ?? '',
              timestamp: parsed.timestamp ?? new Date().toISOString(),
            })
          }
        }
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          handlers.onError?.(err)
        }
      }
    })()

    return () => controller.abort()
  }

  // ── Queue ──

  async getQueueStatus() {
    return this.fetch<any>('/queue/status')
  }

  async getQueueForAgent(role: string) {
    return this.fetch<any>(`/queue/${encodeURIComponent(role)}`)
  }

  // ── Scheduler (extended) ──

  async runSchedulerJob(jobId: string) {
    return this.fetch<any>(`/scheduler/jobs/${encodeURIComponent(jobId)}/run`, { method: 'POST' })
  }

  async toggleScheduler(action: string) {
    return this.fetch<any>(`/scheduler/${encodeURIComponent(action)}`, { method: 'POST' })
  }

  async toggleSchedulerAgent(role: string, action: string) {
    return this.fetch<any>(`/scheduler/agents/${encodeURIComponent(role)}/${encodeURIComponent(action)}`, { method: 'POST' })
  }

  // ── Knowledge ──

  async getKnowledgeDocs() {
    // Knowledge routes are mounted at /api/knowledge with handlers at /,
    // /:id, /search, /upload — NOT /docs. Old client paths 404'd on the
    // server.
    const res = await this.fetch<any>('/knowledge')
    return (res as any)?.documents ?? (res as any)?.docs ?? res
  }

  async searchKnowledge(query: string, role?: string) {
    const body: Record<string, unknown> = { query }
    if (role) body.role = role
    return this.fetch<any>('/knowledge/search', { method: 'POST', body })
  }

  async addKnowledgeDoc(data: Record<string, unknown>) {
    return this.fetch<any>('/knowledge', { method: 'POST', body: data })
  }

  async deleteKnowledgeDoc(id: string) {
    return this.fetch<any>(`/knowledge/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  // ── Lead Gen ──

  async queryLeads(params: Record<string, unknown>): Promise<{ leads: any[]; total: number }> {
    return this.fetch<{ leads: any[]; total: number }>('/leads/query', { method: 'POST', body: params })
  }

  async createLeadList(data: Record<string, unknown>) {
    return this.fetch<any>('/leads/lists', { method: 'POST', body: data })
  }

  // ── Parcel Data ──

  async getParcelDatasets() {
    return this.fetch<any>('/parcels/datasets')
  }

  async getParcelStats() {
    return this.fetch<any>('/parcels/stats')
  }

  async createParcelDataset(data: Record<string, unknown>) {
    return this.fetch<any>('/parcels/datasets', { method: 'POST', body: data })
  }

  async uploadParcelFile(datasetId: string, file: File, columnMapping?: string): Promise<unknown> {
    const token = this.getToken()
    const formData = new FormData()
    formData.append('file', file)
    if (columnMapping) formData.append('columnMapping', columnMapping)

    const res = await fetch(`${API_BASE}/parcels/datasets/${encodeURIComponent(datasetId)}/upload`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    })
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Upload failed' }))
      throw new ApiError(res.status, (error as { error?: string }).error || 'Upload failed')
    }
    return res.json()
  }

  async getParcelDataset(id: string) {
    return this.fetch<any>(`/parcels/datasets/${encodeURIComponent(id)}`)
  }

  async deleteParcelDataset(id: string) {
    return this.fetch<any>(`/parcels/datasets/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  // ── Buy Boxes ──
  // Saved filter sets the user can one-click apply or convert to a
  // named lead list. Shape of filters mirrors listParcels opts.

  async listBuyBoxes(opts?: { target?: 'parcels' | 'leads' | 'listings' }) {
    const q = opts?.target ? `?target=${encodeURIComponent(opts.target)}` : ''
    return this.fetch<{
      boxes: Array<{
        id: string
        name: string
        description: string | null
        target: 'parcels' | 'leads' | 'listings'
        emoji: string | null
        color: string | null
        filters: Record<string, unknown>
        filtersJson: string
        lastAppliedAt: string | null
        lastMatchCount: number | null
        createdBy: string | null
        createdAt: string
        updatedAt: string
      }>
    }>(`/buy-boxes${q}`)
  }

  async createBuyBox(input: {
    name: string
    description?: string
    target?: 'parcels' | 'leads' | 'listings'
    filters: Record<string, unknown>
    emoji?: string
    color?: string
  }) {
    return this.fetch<{ box: { id: string; name: string } }>('/buy-boxes', {
      method: 'POST', body: input,
    })
  }

  async updateBuyBox(id: string, patch: Record<string, unknown>) {
    return this.fetch<{ box: { id: string } }>(`/buy-boxes/${encodeURIComponent(id)}`, {
      method: 'PUT', body: patch,
    })
  }

  async deleteBuyBox(id: string) {
    return this.fetch<{ ok: true }>(`/buy-boxes/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  async applyBuyBox(id: string, limit = 50) {
    return this.fetch<{
      box: { id: string; name: string }
      total: number
      count: number
      parcels: any[]
    }>(`/buy-boxes/${encodeURIComponent(id)}/apply?limit=${limit}`, { method: 'POST', body: {} })
  }

  async convertBuyBoxToLeads(id: string, opts?: { limit?: number }) {
    return this.fetch<{
      box: { id: string; name: string }
      parcelsProcessed: number
      leadsAdded: number
      skipped: number
      listId: string | null
      listName: string | null
    }>(`/buy-boxes/${encodeURIComponent(id)}/convert-to-leads`, {
      method: 'POST', body: opts ?? {},
    })
  }

  async getParcelTriageStats() {
    return this.fetch<{ new: number; imported: number; dismissed: number; archived: number }>(
      '/parcels/triage-stats',
    )
  }

  async dismissParcel(id: string, reason?: string) {
    return this.fetch<{ ok: true }>(`/parcels/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST', body: reason ? { reason } : {},
    })
  }

  async restoreParcel(id: string) {
    return this.fetch<{ ok: true }>(`/parcels/${encodeURIComponent(id)}/restore`, {
      method: 'POST', body: {},
    })
  }

  async bulkDismissParcels(ids: string[], reason?: string) {
    return this.fetch<{ ok: true; dismissed: number }>('/parcels/bulk-dismiss', {
      method: 'POST', body: { ids, ...(reason ? { reason } : {}) },
    })
  }

  async convertParcelsToLeads(filters: Record<string, unknown>) {
    return this.fetch<{
      parcelsProcessed: number
      leadsAdded: number
      skipped: number
      listId: string | null
      listName: string | null
    }>('/parcels/convert-to-leads', { method: 'POST', body: filters })
  }

  // List parcels with filters. Mirror of the API's /api/parcels GET
  // route — used by the parcels browser page to power filtering +
  // conversion-to-leads previews.
  async listParcels(opts?: {
    // Basics
    search?: string
    triage?: 'new' | 'imported' | 'dismissed' | 'archived' | 'all'
    county?: string
    city?: string
    cities?: string                 // comma-separated CSV (case-insensitive IN match)
    propertyType?: string
    datasetId?: string
    minValue?: number
    maxValue?: number
    minYear?: number
    maxYear?: number
    ownerName?: string
    hasCoords?: boolean
    // Last sale window
    minLastSalePrice?: number
    maxLastSalePrice?: number
    minLastSoldYear?: number
    maxLastSoldYear?: number
    // Buildability
    maxElevationChangePct?: number
    minElevationChangePct?: number
    // Zoning bundle
    zoning?: string
    zonings?: string                 // comma-separated CSV (e.g. "R-1,AR-1,PUD")
    zoningType?: string
    zoningSubtype?: string
    usecode?: string
    usedesc?: string
    subdivision?: string
    neighborhood?: string
    schoolDistrict?: string
    // Lot / structure
    minAcres?: number
    maxAcres?: number
    minBedrooms?: number
    maxBedrooms?: number
    minBldgCount?: number
    maxBldgCount?: number
    // Ownership signals
    absenteeOwner?: boolean
    outOfStateOwner?: boolean
    outOfCountyOwner?: boolean
    ownerOccupied?: boolean
    ownType?: string
    individualOwner?: boolean
    minOwnershipYears?: number
    maxOwnershipYears?: number
    // Infill / assemblage filters
    maxOwnerParcelCount?: number
    maxOwnerSubdivisionParcelCount?: number
    // Subdivision intelligence
    minSubdivisionLots?: number
    minSubdivisionExtraLots?: number
    subdivisionConfidence?: 'high' | 'medium' | 'low'
    subdivisionRuleApplied?: string
    // Vacancy
    vacant?: boolean
    uspsVacancy?: string
    // Flood / risk
    excludeFloodZones?: string  // comma-separated codes (e.g. "A,V")
    femaFloodZone?: string
    femaNriRiskRating?: string
    // Scores / qoz
    insiteScoreMin?: 'low' | 'medium' | 'high'
    inQoz?: boolean
    // Valuation breakdown
    minLandValue?: number
    maxLandValue?: number
    minImprovValue?: number
    maxImprovValue?: number
    minParcelValue?: number
    maxParcelValue?: number
    limit?: number
    offset?: number
  }) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(opts ?? {})) {
      if (v !== undefined && v !== null && v !== '' && v !== false) params.set(k, String(v))
    }
    const q = params.toString()
    return this.fetch<{
      parcels: Array<{
        id: string
        orgId: string
        parcelId: string | null
        address: string
        city: string | null
        county: string
        state: string
        zip: string | null
        ownerName: string | null
        ownerAddress: string | null
        ownerCity: string | null
        ownerState: string | null
        ownerZip: string | null
        ownerOccupied: boolean | null
        propertyType: string | null
        landUse: string | null
        bedrooms: number | null
        bathrooms: number | null
        sqft: number | null
        lotSize: number | null
        yearBuilt: number | null
        assessedValue: number | null
        marketValue: number | null
        taxAmount: number | null
        lastSaleDate: string | null
        lastSalePrice: number | null
        latitude: number | null
        longitude: number | null
        datasetId: string | null
        // Regrid-rich
        zoning: string | null
        zoningDescription: string | null
        zoningType: string | null
        zoningSubtype: string | null
        zoningCodeLink: string | null
        usecode: string | null
        usedesc: string | null
        numStories: number | null
        numUnits: number | null
        numRooms: number | null
        numBath: number | null
        numBedrooms: number | null
        structStyle: string | null
        deededAcres: number | null
        gisAcre: number | null
        bldgFootprintSqft: number | null
        bldgCount: number | null
        improvValue: number | null
        landValue: number | null
        parcelValue: number | null
        agValue: number | null
        homesteadExemption: number | null
        taxYear: number | null
        ownType: string | null
        previousOwner: string | null
        lastOwnershipTransferDate: string | null
        absenteeOwner: boolean | null
        outOfStateOwner: boolean | null
        outOfCountyOwner: boolean | null
        ownershipYears: number | null
        femaFloodZone: string | null
        femaFloodZoneSubtype: string | null
        femaNriRiskRating: string | null
        uspsVacancy: string | null
        uspsVacancyDate: string | null
        subdivision: string | null
        neighborhood: string | null
        legalDesc: string | null
        placekey: string | null
        llUuid: string | null
        lbcsFunctionDesc: string | null
        lbcsStructureDesc: string | null
        lbcsSiteDesc: string | null
        insiteScore: string | null
        medianHouseholdIncome: number | null
        populationDensity: number | null
        inQoz: boolean | null
        schoolDistrict: string | null
        padusPublicAccess: string | null
        ownerParcelCount: number | null
        ownerSubdivisionCount: number | null
        elevationChangePct: number | null
        subdivisionPotentialLots: number | null
        subdivisionExtraLots: number | null
        subdivisionRuleApplied: string | null
        subdivisionConfidence: string | null
        subdivisionMinLotSqft: number | null
        triageStatus: 'new' | 'imported' | 'dismissed' | 'archived'
        importedLeadId: string | null
        importedAt: string | null
        dismissedAt: string | null
        dismissedReason: string | null
        createdAt: string
      }>
      total: number
      limit: number
      offset: number
    }>(`/parcels${q ? `?${q}` : ''}`)
  }

  // ── API Keys ──

  async getApiKeys() {
    const res = await this.fetch<any>('/settings/api-keys')
    return (res as any)?.keys ?? res
  }

  async createApiKey(name: string): Promise<{ key: any }> {
    return this.fetch<{ key: any }>('/settings/api-keys', { method: 'POST', body: { name } })
  }

  async deleteApiKey(id: string) {
    return this.fetch<any>(`/settings/api-keys/${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  // ── Notification Settings ──

  async getNotificationSettings() {
    return this.fetch<{
      preferences: NotificationPrefs
      defaults: NotificationPrefs
      channelAvailability: Record<NotificationChannel, { available: boolean; reason?: string }>
    }>('/settings/notifications')
  }

  async updateNotificationSettings(prefs: Partial<NotificationPrefs>) {
    return this.fetch<{ success: boolean; preferences: NotificationPrefs }>(
      '/settings/notifications',
      { method: 'PUT', body: prefs },
    )
  }

  // ── Org Settings ──

  async getOrgDetail() {
    return this.fetch<{
      org: {
        id: string
        name: string
        plan: string
        billingEmail: string | null
        website: string | null
        phone: string | null
        addressLine1: string | null
        addressLine2: string | null
        city: string | null
        state: string | null
        zip: string | null
        timezone: string
        taxId: string | null
        archivedAt: string | null
        createdAt: string
      }
      stats: {
        members: number
        contacts: number
        leads: number
        deals: number
      }
    }>('/settings/org')
  }

  async updateOrg(data: {
    orgName?: string
    billingEmail?: string | null
    website?: string | null
    phone?: string | null
    addressLine1?: string | null
    addressLine2?: string | null
    city?: string | null
    state?: string | null
    zip?: string | null
    timezone?: string
    taxId?: string | null
  }) {
    return this.fetch<{ success: boolean; org: any }>('/settings/org', { method: 'PUT', body: data })
  }

  async archiveOrg() {
    return this.fetch<{ success: boolean }>('/settings/org/archive', { method: 'POST', body: {} })
  }

  async unarchiveOrg() {
    return this.fetch<{ success: boolean }>('/settings/org/unarchive', { method: 'POST', body: {} })
  }

  // ── Team Settings ──

  async getTeamMembers(): Promise<any[]> {
    const res = await this.fetch<any>('/settings/team')
    return (res as any)?.members ?? (Array.isArray(res) ? res : [])
  }

  async removeTeamMember(userId: string) {
    return this.fetch<any>(`/settings/team/${encodeURIComponent(userId)}`, { method: 'DELETE' })
  }

  async updateTeamMemberRole(userId: string, role: string) {
    return this.fetch<any>(`/settings/team/${encodeURIComponent(userId)}/role`, { method: 'PUT', body: { role } })
  }

  async inviteTeamMember(email: string, role: string): Promise<{ invite: TeamInvite }> {
    return this.fetch<{ invite: TeamInvite }>('/settings/team/invite', { method: 'POST', body: { email, role } })
  }

  async getTeamInvites(): Promise<TeamInvite[]> {
    const res = await this.fetch<{ invites: TeamInvite[] }>('/settings/team/invites')
    return res.invites ?? []
  }

  async resendTeamInvite(inviteId: string): Promise<{ invite: TeamInvite }> {
    return this.fetch<{ invite: TeamInvite }>(
      `/settings/team/invites/${encodeURIComponent(inviteId)}/resend`,
      { method: 'POST' },
    )
  }

  async revokeTeamInvite(inviteId: string): Promise<{ success: boolean }> {
    return this.fetch<{ success: boolean }>(
      `/settings/team/invites/${encodeURIComponent(inviteId)}/revoke`,
      { method: 'POST' },
    )
  }

  /** Public — used by /signup to validate an invite token before showing the form. */
  async lookupInvite(token: string) {
    return this.fetch<{
      invite: { email: string; role: string; orgName: string; expiresAt: string | null; userExists: boolean }
    }>(`/auth/invites/${encodeURIComponent(token)}`)
  }

  /** Signed-in — accept an invite when you already have an account. */
  async acceptInviteSignedIn(token: string) {
    return this.fetch<{ success: boolean; orgId: string; role: string }>(
      `/auth/invites/${encodeURIComponent(token)}/accept`,
      { method: 'POST' },
    )
  }

  // ── Admin ──

  async getAdminUsage() {
    return this.fetch<any>('/admin/usage')
  }

  async setOrgPlan(orgId: string, plan: string) {
    return this.fetch<any>(`/admin/orgs/${encodeURIComponent(orgId)}/plan`, { method: 'PUT', body: { plan } })
  }

  async resetOrgUsage(orgId: string) {
    return this.fetch<any>(`/admin/orgs/${encodeURIComponent(orgId)}/reset-usage`, { method: 'POST' })
  }

  async monthlyReset() {
    return this.fetch<any>('/admin/monthly-reset', { method: 'POST' })
  }

  // ── Calendar / Scheduling ──

  async getCalendarStatus() {
    return this.fetch<{
      connected: boolean
      provider?: string
      externalCalendarId?: string
      timezone?: string
      businessHoursStart?: number
      businessHoursEnd?: number
      businessDays?: string
      defaultDurationMin?: number
      bufferMin?: number
      isActive?: boolean
      boundUserId?: string
    }>(`/calendar/status`)
  }

  async startCalendarAuth() {
    return this.fetch<{ url: string }>(`/calendar/auth/start`, { method: 'POST' })
  }

  async updateCalendarSettings(settings: {
    timezone?: string
    businessHoursStart?: number
    businessHoursEnd?: number
    businessDays?: string
    defaultDurationMin?: number
    bufferMin?: number
    isActive?: boolean
  }) {
    return this.fetch<{ ok: true }>(`/calendar/settings`, {
      method: 'PUT',
      body: settings,
    })
  }

  async disconnectCalendar() {
    return this.fetch<{ ok: true }>(`/calendar/binding`, { method: 'DELETE' })
  }

  async listAppointments(from?: string, to?: string) {
    const params = new URLSearchParams()
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    const q = params.toString()
    return this.fetch<{
      appointments: Array<{
        id: string
        scheduledAt: string
        durationMin: number
        status: 'scheduled' | 'completed' | 'no_show' | 'cancelled'
        prospectName: string | null
        prospectPhone: string | null
        prospectEmail: string | null
        prospectAddress: string | null
        notes: string | null
        externalEventUrl: string | null
        source: 'auto' | 'manual' | 'inbound'
        threadId: string | null
        leadId: string | null
        dealId: string | null
      }>
    }>(`/calendar/appointments${q ? `?${q}` : ''}`)
  }

  async cancelAppointment(id: string) {
    return this.fetch<{ ok: true }>(`/calendar/appointments/${id}`, { method: 'DELETE' })
  }

  async updateAppointmentStatus(id: string, status: 'scheduled' | 'completed' | 'no_show' | 'cancelled') {
    return this.fetch<{ ok: true }>(`/calendar/appointments/${id}/status`, {
      method: 'PATCH',
      body: { status },
    })
  }

  async findCalendarSlots(opts?: { daysAhead?: number; durationMin?: number; limit?: number }) {
    const params = new URLSearchParams()
    if (opts?.daysAhead != null) params.set('daysAhead', String(opts.daysAhead))
    if (opts?.durationMin != null) params.set('durationMin', String(opts.durationMin))
    if (opts?.limit != null) params.set('limit', String(opts.limit))
    const q = params.toString()
    return this.fetch<{
      slots: Array<{ id: string; start: string; end: string; label: string }>
    }>(`/calendar/slots${q ? `?${q}` : ''}`)
  }

  async createAppointment(input: {
    scheduledAt: string
    durationMin?: number
    threadId?: string
    leadId?: string
    dealId?: string
    prospectName?: string
    prospectPhone?: string
    prospectEmail?: string
    prospectAddress?: string
    notes?: string
    summary?: string
  }) {
    return this.fetch<{ ok: true; appointmentId: string; eventUrl?: string }>(
      `/calendar/appointments`,
      { method: 'POST', body: input },
    )
  }

  // ── Data sources (per-org registry) ──

  async listDataSources() {
    return this.fetch<{
      sources: Array<{
        id: string
        name: string
        slug: string
        kind: string
        configJson: string | null
        isBuiltin: boolean
        isActive: boolean
      }>
    }>(`/data-sources`)
  }

  async createDataSource(input: {
    name: string
    slug: string
    kind: 'zillow' | 'redfin' | 'realtor' | 'mls' | 'facebook_group' | 'facebook_marketplace' | 'rss' | 'html-template' | 'api-template' | 'custom'
    config?: Record<string, unknown>
    isActive?: boolean
  }) {
    return this.fetch<{ source: { id: string } }>(`/data-sources`, {
      method: 'POST',
      body: input,
    })
  }

  async updateDataSource(id: string, patch: Record<string, unknown>) {
    return this.fetch<{ source: { id: string } }>(`/data-sources/${id}`, {
      method: 'PUT',
      body: patch,
    })
  }

  async deleteDataSource(id: string) {
    return this.fetch<{ ok: true }>(`/data-sources/${id}`, { method: 'DELETE' })
  }

  // AI feed-builder draft
  async draftListingFeed(prompt: string) {
    return this.fetch<{
      draft: {
        name: string
        areaType: 'city' | 'county' | 'zip'
        areaValue: string
        state: string
        sources: string[]
        cadenceMin: number
        filters: Record<string, unknown>
        rationale: string
      }
    }>(`/listing-monitor/feeds/draft`, {
      method: 'POST',
      body: { prompt },
    })
  }

  // ── Listings (triage dashboard) ──

  async listListings(opts?: {
    triage?: 'new' | 'imported' | 'dismissed' | 'all'
    source?: string
    status?: string
    propertyType?: string
    city?: string
    minPrice?: number
    maxPrice?: number
    minBeds?: number
    maxBeds?: number
    minSqft?: number
    maxSqft?: number
    minYearBuilt?: number
    maxYearBuilt?: number
    minDom?: number
    maxDom?: number
    minMotivation?: number
    minPriceDrops?: number
    droppedSince?: string  // ISO timestamp — only listings with lastPriceDropAt >= this
    minTotalReduction?: number
    spreadMinPct?: number  // (list-sold)/sold * 100 ≥ X. Negative = underwater.
    spreadMaxPct?: number  // (list-sold)/sold * 100 ≤ X.
    // Tier-3 filters
    zoning?: string
    propertyCondition?: string
    schoolDistrict?: string
    subdivision?: string
    listingTerms?: string
    minGarageSpaces?: number
    maxClimateFlood?: number
    maxClimateFire?: number
    maxClimateHeat?: number
    maxClimateWind?: number
    maxClimateAir?: number
    minTaxAnnual?: number
    maxTaxAnnual?: number
    // Ownership tenure & last-sold history
    minYearsOwned?: number
    maxYearsOwned?: number
    minLastSoldPrice?: number
    maxLastSoldPrice?: number
    minLastSoldYear?: number
    maxLastSoldYear?: number
    // Financials
    minZestimate?: number
    maxZestimate?: number
    minRentZestimate?: number
    minGrossYieldPct?: number
    minEquityPercent?: number
    maxMonthlyHoaFee?: number
    noHoa?: boolean
    minPricePerSqft?: number
    maxPricePerSqft?: number
    // Structural extras + missing min/max
    minBaths?: number
    maxBaths?: number
    minLotSqft?: number
    maxLotSqft?: number
    // Engagement & flags
    minValueAddScore?: number
    hasValueAddScan?: boolean
    minViewCount?: number
    maxViewCount?: number
    minSaveCount?: number
    minPhotoCount?: number
    maxPhotoCount?: number
    isFlippedWithin12Mo?: boolean
    isListedByMgmtCo?: boolean
    excludeFlips?: boolean
    listingSubType?: string  // 'foreclosure' | 'auction' | 'reo' | 'short_sale' | 'estate' | 'any_distress'
    freshness?: 'fresh' | 'stale' | 'off_feed'
    hasAgentContact?: boolean
    county?: string
    search?: string
    sortBy?: 'listed_at' | 'price' | 'dom' | 'first_seen' | 'motivation' | 'beds' | 'sqft' | 'address' | 'price_drops' | 'recent_drop' | 'total_reduction' | 'zoning' | 'lot_sqft' | 'years_owned'
    sortOrder?: 'asc' | 'desc'
    limit?: number
    offset?: number
  }) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(opts ?? {})) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v))
    }
    const q = params.toString()
    return this.fetch<{
      listings: Array<{
        id: string
        address: string
        city: string | null
        state: string | null
        zip: string | null
        county: string | null
        listPrice: number | null
        originalListPrice: number | null
        beds: number | null
        baths: number | null
        sqft: number | null
        yearBuilt: number | null
        propertyType: string | null
        status: string
        daysOnMarket: number | null
        photoUrl: string | null
        externalIdsJson: string
        listingAgentName: string | null
        listingAgentPhone: string | null
        listingAgentEmail: string | null
        zestimate: number | null
        lastSoldPrice: number | null
        lastSoldAt: string | null
        triageStatus: 'new' | 'imported' | 'dismissed' | 'archived'
        importedLeadId: string | null
        firstSeenAt: string
        lastSeenAt: string
        listedAt: string | null
        motivationScore: number | null
        motivationSignals: string | null
        priceDropCount: number
        lastPriceDropAt: string | null
        totalPriceReduction: number
        // Tier-3 scalars (in slim projection — drive the list-view badges).
        zoning: string | null
        zoningSource: string | null
        zoningDensity: string | null
        propertyCondition: string | null
        listingSubType: string | null
        schoolDistrict: string | null
        taxAnnualAmount: number | null
        climateFloodScore: number | null
        climateFireScore: number | null
        climateHeatScore: number | null
        climateWindScore: number | null
        climateAirScore: number | null
        garageSpaces: number | null
        // Extras now in slim projection — drive ownership / yield / flag
        // badges and "no HOA + acreage" scans without per-row detail.
        rentZestimate: number | null
        lotSqft: number | null
        monthlyHoaFee: number | null
        valueAddScore: number | null
        valueAddAnalyzedAt: string | null
        isFlippedWithin12Mo: boolean | null
        isListedByMgmtCo: boolean | null
        pricePerSqft: number | null
        viewCount: number | null
        saveCount: number | null
        photoCount: number | null
        // Derived at query time. BD discovery only returns currently-active
        // listings; once lastSeenAt drifts the listing has likely
        // transitioned (pending/sold/expired) and dropped from the feed.
        freshness: 'fresh' | 'stale' | 'off_feed'
        daysSinceSeen: number | null
      }>
      total: number
      limit: number
      offset: number
    }>(`/listings${q ? `?${q}` : ''}`)
  }

  async getListingStats() {
    return this.fetch<{ new: number; imported: number; dismissed: number; archived: number }>(
      `/listings/stats/summary`,
    )
  }

  async getListing(id: string) {
    return this.fetch<{
      listing: {
        id: string
        orgId: string
        feedId: string | null
        address: string
        addressNormalized: string
        city: string | null
        state: string | null
        zip: string | null
        county: string | null
        latitude: number | null
        longitude: number | null
        listPrice: number | null
        originalListPrice: number | null
        pricePerSqft: number | null
        beds: number | null
        baths: number | null
        sqft: number | null
        yearBuilt: number | null
        propertyType: string | null
        status: string
        daysOnMarket: number | null
        description: string | null
        photoUrl: string | null
        externalIdsJson: string
        listingAgentName: string | null
        listingAgentPhone: string | null
        listingAgentEmail: string | null
        zestimate: number | null
        rentZestimate: number | null
        lastSoldPrice: number | null
        lastSoldAt: string | null
        taxAssessedValue: number | null
        taxAssessedYear: number | null
        propertyTaxRate: number | null
        lotSqft: number | null
        monthlyHoaFee: number | null
        photoCount: number | null
        photosJson: string | null
        valueAddScore: number | null
        valueAddSignals: string | null
        valueAddAnalyzedAt: string | null
        priceHistoryJson: string | null
        // Tier-3 structured facts from BD's resoFacts/climate bags.
        zoning: string | null
        propertyCondition: string | null
        parcelNumber: string | null
        subdivision: string | null
        mlsNumber: string | null
        schoolDistrict: string | null
        taxAnnualAmount: number | null
        climateFloodScore: number | null
        climateFireScore: number | null
        climateWindScore: number | null
        climateHeatScore: number | null
        climateAirScore: number | null
        garageSpaces: number | null
        parkingFeatures: string | null
        heating: string | null
        cooling: string | null
        basement: string | null
        listingTerms: string | null
        resoFactsJson: string | null
        schoolsJson: string | null
        taxHistoryJson: string | null
        climateRiskJson: string | null
        motivationScore: number | null
        motivationSignals: string | null
        priceDropCount: number
        lastPriceDropAt: string | null
        totalPriceReduction: number
        triageStatus: 'new' | 'imported' | 'dismissed' | 'archived'
        importedLeadId: string | null
        importedAt: string | null
        dismissedAt: string | null
        firstSeenAt: string
        lastSeenAt: string
        listedAt: string | null
      }
      events: Array<{
        id: string
        listingId: string
        eventType: string
        source: string | null
        deltaJson: string
        createdAt: string
      }>
    }>(`/listings/${id}`)
  }

  async importListing(id: string) {
    return this.fetch<{ ok: true; leadId: string; alreadyImported?: boolean }>(
      `/listings/${id}/import`,
      { method: 'POST' },
    )
  }

  async dismissListing(id: string, reason?: string) {
    return this.fetch<{ ok: true }>(`/listings/${id}/dismiss`, {
      method: 'POST',
      body: reason ? { reason } : {},
    })
  }

  async restoreListing(id: string) {
    return this.fetch<{ ok: true }>(`/listings/${id}/restore`, { method: 'POST' })
  }

  async scanListingValueAdd(id: string) {
    return this.fetch<{
      ok: true
      score: number
      summary: string
      signals: Array<{
        type:
          | 'dated_kitchen' | 'dated_bathroom' | 'original_flooring'
          | 'popcorn_ceiling' | 'dated_fixtures' | 'wallpaper_or_paint'
          | 'dated_hvac' | 'dated_appliances' | 'old_roof' | 'old_windows'
          | 'overgrown_yard' | 'peeling_paint' | 'aging_siding' | 'cracked_driveway'
          | 'roof_damage' | 'boarded_windows' | 'hoarding_visible'
          | 'foundation_cracks' | 'fire_or_water_damage' | 'vacant_indicators'
          | 'deferred_maintenance'
        confidence: number
        photoIndex: number
        evidence: string
      }>
      photosAnalyzed: number
      analyzedAt: string
    }>(`/listings/${id}/scan-value-add`, { method: 'POST' })
  }

  async bulkScanListingValueAdd(opts?: {
    ids?: string[]
    max?: number
    onlyMissing?: boolean
  }) {
    return this.fetch<{
      ok: true
      scanned: number
      succeeded: number
      failed: number
      results: Array<{
        id: string
        address: string
        ok: boolean
        score?: number
        signalCount?: number
        error?: string
      }>
    }>('/listings/scan-value-add/bulk', {
      method: 'POST',
      body: opts ?? {},
    })
  }

  async refetchListingPhotosFromSource(max?: number) {
    const q = max ? `?max=${max}` : ''
    return this.fetch<{
      ok: boolean
      snapshotId?: string
      picked?: number
      triggered?: number
      returned?: number
      normalized?: number
      ingested?: { total: number; new: number; changed: number; unchanged: number }
      message?: string
      error?: string
      hint?: string
    }>(`/listings/photos/refetch-from-source${q}`, { method: 'POST' })
  }

  async backfillListingPhotos(opts?: { onlyMissing?: boolean; limit?: number }) {
    const params = new URLSearchParams()
    if (opts?.onlyMissing === false) params.set('onlyMissing', 'false')
    if (opts?.limit) params.set('limit', String(opts.limit))
    const q = params.toString()
    return this.fetch<{
      ok: true
      scanned: number
      updated: number
      skipped: number
      totalPhotos: number
      avgPerListing: number
      onlyMissing: boolean
    }>(`/listings/photos/backfill${q ? `?${q}` : ''}`, { method: 'POST' })
  }

  async backfillMotivationScores(opts?: { onlyNull?: boolean; limit?: number }) {
    const params = new URLSearchParams()
    if (opts?.onlyNull === false) params.set('onlyNull', 'false')
    if (opts?.limit) params.set('limit', String(opts.limit))
    const q = params.toString()
    return this.fetch<{ ok: true; scanned: number; updated: number; onlyNull: boolean }>(
      `/listings/score/backfill${q ? `?${q}` : ''}`,
      { method: 'POST' },
    )
  }

  async backfillPriceDrops(opts?: { onlyZero?: boolean; limit?: number }) {
    const params = new URLSearchParams()
    if (opts?.onlyZero === false) params.set('onlyZero', 'false')
    if (opts?.limit) params.set('limit', String(opts.limit))
    const q = params.toString()
    return this.fetch<{ ok: true; scanned: number; updated: number; withDrops: number; onlyZero: boolean }>(
      `/listings/price-drops/backfill${q ? `?${q}` : ''}`,
      { method: 'POST' },
    )
  }

  async bulkImportListings(ids: string[], opts?: { createList?: boolean; listName?: string }) {
    return this.fetch<{
      ok: true
      imported: number
      skipped: number
      listId: string | null
      leadIds: string[]
    }>(`/listings/bulk-import`, {
      method: 'POST',
      body: { ids, ...(opts ?? {}) },
    })
  }

  /**
   * Build a lead_list from the current listings filter set instead of
   * cherry-picking ids. Server resolves filters → matching listings →
   * imports them. Capped at maxFromFilters (default 500, max 1000).
   */
  async bulkImportListingsByFilters(
    filters: {
      triage?: 'new' | 'imported' | 'dismissed' | 'archived' | 'all'
      source?: string
      status?: string
      propertyType?: string
      city?: string
      minPrice?: number
      maxPrice?: number
      minBeds?: number
      maxBeds?: number
      minSqft?: number
      maxSqft?: number
      minYearBuilt?: number
      maxYearBuilt?: number
      minDom?: number
      maxDom?: number
      spreadMinPct?: number
      spreadMaxPct?: number
      minMotivation?: number
      minPriceDrops?: number
      droppedSince?: string
      minTotalReduction?: number
      // Tier-3 filters
      zoning?: string
      propertyCondition?: string
      schoolDistrict?: string
      subdivision?: string
      listingTerms?: string
      minGarageSpaces?: number
      maxClimateFlood?: number
      maxClimateFire?: number
      maxClimateHeat?: number
      maxClimateWind?: number
      maxClimateAir?: number
      minTaxAnnual?: number
      maxTaxAnnual?: number
      // Ownership / financial / structural extras
      minYearsOwned?: number
      maxYearsOwned?: number
      minLastSoldPrice?: number
      maxLastSoldPrice?: number
      minLastSoldYear?: number
      maxLastSoldYear?: number
      minZestimate?: number
      maxZestimate?: number
      minRentZestimate?: number
      minGrossYieldPct?: number
      minEquityPercent?: number
      maxMonthlyHoaFee?: number
      noHoa?: boolean
      minPricePerSqft?: number
      maxPricePerSqft?: number
      minBaths?: number
      maxBaths?: number
      minLotSqft?: number
      maxLotSqft?: number
      county?: string
      // Engagement & flags
      minValueAddScore?: number
      hasValueAddScan?: boolean
      minViewCount?: number
      maxViewCount?: number
      minSaveCount?: number
      minPhotoCount?: number
      maxPhotoCount?: number
      isFlippedWithin12Mo?: boolean
      isListedByMgmtCo?: boolean
      excludeFlips?: boolean
      listingSubType?: string
      freshness?: 'fresh' | 'stale' | 'off_feed'
      hasAgentContact?: boolean
      search?: string
    },
    opts?: { createList?: boolean; listName?: string; maxFromFilters?: number },
  ) {
    return this.fetch<{
      ok: true
      imported: number
      skipped: number
      listId: string | null
      leadIds: string[]
      resolvedFromFilters: number
    }>(`/listings/bulk-import`, {
      method: 'POST',
      body: { filters, ...(opts ?? {}) },
    })
  }

  // Listing-monitor feeds + jobs
  async listListingFeeds() {
    return this.fetch<{
      feeds: Array<{
        id: string
        name: string
        areaType: 'city' | 'county' | 'zip'
        areaValue: string
        state: string
        sources: string
        cadenceMin: number
        isActive: boolean
        filtersJson: string | null
        lastTickedAt: string | null
        nextTickAt: string | null
      }>
    }>(`/listing-monitor/feeds`)
  }

  async createListingFeed(input: {
    name: string
    areaType: 'city' | 'county' | 'zip'
    areaValue: string
    state: string
    sources: Array<'zillow' | 'redfin' | 'realtor' | 'mls'>
    cadenceMin?: number
    filters?: Record<string, unknown>
    isActive?: boolean
  }) {
    return this.fetch<{ feed: { id: string } }>(`/listing-monitor/feeds`, {
      method: 'POST',
      body: input,
    })
  }

  async updateListingFeed(id: string, patch: Record<string, unknown>) {
    return this.fetch<{ feed: { id: string } }>(`/listing-monitor/feeds/${id}`, {
      method: 'PUT',
      body: patch,
    })
  }

  async deleteListingFeed(id: string) {
    return this.fetch<{ ok: true }>(`/listing-monitor/feeds/${id}`, { method: 'DELETE' })
  }

  async runListingFeed(id: string) {
    return this.fetch<{ ok: true; jobIds: string[]; queued: number }>(
      `/listing-monitor/feeds/${id}/run`,
      { method: 'POST' },
    )
  }

  async cancelListingMonitorJob(id: string) {
    return this.fetch<{ ok: true }>(`/listing-monitor/jobs/${id}/cancel`, { method: 'POST' })
  }

  async deleteListingMonitorJob(id: string) {
    return this.fetch<{ ok: true }>(`/listing-monitor/jobs/${id}`, { method: 'DELETE' })
  }

  async tickListingMonitor() {
    return this.fetch<{
      feedsConsidered: number
      feedsTicked: number
      jobsQueued: number
      skippedNoSources: number
      errors: Array<{ feedId: string; reason: string }>
    }>(`/listing-monitor/tick`, { method: 'POST' })
  }

  async listListingMonitorJobs(opts?: { status?: string; feedId?: string }) {
    const params = new URLSearchParams()
    if (opts?.status) params.set('status', opts.status)
    if (opts?.feedId) params.set('feedId', opts.feedId)
    const q = params.toString()
    return this.fetch<{
      jobs: Array<{
        id: string
        feedId: string
        source: string
        status: string
        progress: number
        message: string
        listingsFound: number
        newListings: number
        changedListings: number
        createdAt: string
        completedAt: string | null
        error: string | null
      }>
    }>(`/listing-monitor/jobs${q ? `?${q}` : ''}`)
  }

  // ── Health ──

  async healthCheck(): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE.replace('/api', '')}/health`)
      return res.ok
    } catch {
      return false
    }
  }
}

// Singleton instance
export const api = new ForgeAPI()
export { ApiError }
